/**
 * Console API service layer — the browser wiring that connects the operator
 * console screens to the AVOS M1 Property & Inventory HTTP API.
 *
 * Web module boundary: this file imports NO backend runtime. It only speaks to
 * the API over `fetch`, exchanging plain JSON, following the existing
 * `client/services.ts` `postJson` pattern. Shared TYPES may come from
 * `@avos/foundation` (type-only). The demo API server
 * (`packages/web/scripts/api-inventory.mjs`) translates these HTTP calls into
 * real `@avos/core-api` inventory operations against Postgres.
 *
 * Money is always integer MINOR units on the wire (e.g. 1200000 = ₹12,000.00).
 * Divide by 100 for display; format with {@link formatMoney}.
 */
import type { ErrorEnvelope } from "@avos/foundation";

/** POST JSON to an API path, returning the parsed JSON body. */
async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  // Parse JSON defensively: a non-JSON / empty body (e.g. a proxy 5xx) must not
  // throw an opaque "Unexpected token" error. Surface a proper error envelope
  // so the UI shows a clear what/why/next instead of a generic failure.
  const text = await res.text();
  if (text.trim() === "") {
    if (!res.ok) {
      return { error: { error: {
        code: `HTTP_${res.status}`,
        message: "The server could not complete the request.",
        detail: `The request failed with status ${res.status}.`,
        next: "Try again. If this persists, contact support.",
        request_id: "none",
      } } } as T;
    }
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    return { error: { error: {
      code: `HTTP_${res.status}`,
      message: "The server returned an unexpected response.",
      detail: "The response could not be read as JSON.",
      next: "Try again. If this persists, contact support.",
      request_id: "none",
    } } } as T;
  }
}

/** A create-style response: either a new id, or a backend error envelope. */
export type CreateResult = { readonly id: string } | ErrorEnvelope;

/** Narrow a create/set response to its error envelope, if it is one. */
export function asError(value: unknown): ErrorEnvelope | null {
  if (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as { error: unknown }).error === "object"
  ) {
    return value as ErrorEnvelope;
  }
  return null;
}

/**
 * Sign the current operator out: POST /api/auth/logout. The server identifies
 * the CURRENT session from the HttpOnly cookie (never a browser-supplied id),
 * revokes that persistent_session, and clears the auth cookie. Idempotent — a
 * missing/already-revoked session still returns 200. `credentials: "include"`
 * guarantees the cookie is sent so the correct session is revoked.
 *
 * Returns `true` only when the server confirms it processed the logout (so the
 * caller never claims revocation that did not happen); `false` on a network/
 * server error, letting the UI surface a retry instead of silently "logging
 * out" on the client only.
 */
export async function apiLogout(): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: "{}",
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Domain types (mirror the JSON API response shapes).
// ---------------------------------------------------------------------------

/** Dashboard summary metrics. */
export interface InventorySummary {
  readonly propertyCount: number;
  readonly unitCount: number;
  readonly totalCapacity: number;
  readonly adrMinor: number;
  readonly currency: string;
}

/** A property record. Distribution/settings fields mirror the Channex config. */
export interface Property {
  readonly id: string;
  readonly name: string;
  readonly timezone: string;
  readonly type: string | null;
  readonly address: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly country: string | null;
  readonly currency: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly website: string | null;
  readonly postcode: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly minPriceMinor: string | null;
  readonly maxPriceMinor: string | null;
  readonly autoupdateOnModification: boolean;
  readonly autoupdateOnCancellation: boolean;
  readonly minStayType: string;
  readonly inventoryDays: number;
  readonly cutOffTime: string;
  readonly cutOffDays: number;
  readonly maxAdvanceDays: number | null;
  /**
   * The stored/reconciled OTA-distribution state (Stage 1, migration 053).
   * Optional to stay backward-compatible with any cached property shape that
   * predates the column.
   */
  readonly distributionState?: DistributionState;
  readonly createdAt: string;
}

/**
 * The VISIBLE OTA-distribution state for a property. Mirrors core-api's
 * reconciled 6-state `DistributionState` (migration 053 + Stage 1), so the wire
 * values emitted by the backend are identical to the backend's:
 *   - `draft`          — saved as a draft; provisioning deferred (never pushed).
 *   - `setup_required` — created/editable but the readiness checklist is unmet
 *                        (replaces the legacy `not_ready`).
 *   - `provisioning`   — mid-provisioning at the channel manager.
 *   - `ready`          — provider ready AND the readiness checklist passes.
 *   - `sync_failed`    — the provider mapping is in error (replaces `error`).
 *   - `archived`       — the property is decommissioned.
 *
 * BACK-COMPAT: the legacy 4-state literals `not_ready` and `error` are retained
 * as accepted aliases because the existing (Stage-3-owned) PropertiesScreen pill
 * helpers still switch on them. The BACKEND NEVER EMITS these two — it only ever
 * returns the 6 reconciled states above (`setup_required`/`sync_failed` in place
 * of `not_ready`/`error`). The aliases keep the current UI compiling until Stage
 * 3 migrates the pills to the full 6-state set; remove them then.
 */
export type DistributionState =
  | "draft"
  | "setup_required"
  | "provisioning"
  | "ready"
  | "sync_failed"
  | "archived"
  // Legacy aliases (never emitted by the backend; see note above).
  | "not_ready"
  | "error";

/** Distribution status returned alongside create/update/get. */
export interface DistributionStatus {
  readonly state: DistributionState;
  readonly lastError?: string | null;
  readonly reason?: string | null;
}

/** Per-property list enrichment (status + units + connected channels). */
export interface PropertyDistribution {
  readonly propertyId: string;
  readonly unitCount: number;
  readonly distributionState: DistributionState;
  readonly connectedChannels: readonly string[];
  /** Human-readable labels of the readiness items still outstanding, if any. */
  readonly missing?: readonly string[];
}

/** The full editable property + distribution form payload (drawer). */
export interface PropertyFormInput {
  readonly name: string;
  readonly currency: string;
  readonly type: string;
  readonly email: string;
  readonly phone: string;
  readonly website?: string;
  readonly country: string;
  readonly timezone: string;
  readonly postcode: string;
  readonly state: string;
  readonly city: string;
  readonly address: string;
  readonly latitude?: number | null;
  readonly longitude?: number | null;
  // Distribution settings (prices as decimal strings; blank = no limit).
  readonly minPrice?: string;
  readonly maxPrice?: string;
  readonly autoupdateOnModification?: boolean;
  readonly autoupdateOnCancellation?: boolean;
  readonly minStayType?: "both" | "arrival" | "through";
  readonly inventoryDays?: number;
  readonly cutOffTime?: string;
  readonly cutOffDays?: number;
  readonly maxAdvanceDays?: number | null;
  /**
   * When true, the property is SAVED AS A DRAFT: it is created with
   * distribution_state='draft' and provisioning is DEFERRED (never pushed to the
   * channel manager). `apiCreateProperty` forwards this flag as-is. Omit/false
   * for the normal "Create property" path (create + provision).
   */
  readonly draft?: boolean;
}

/** A geocode lookup result. */
export interface GeocodeResult {
  readonly latitude: number;
  readonly longitude: number;
  readonly city: string | null;
  readonly state: string | null;
  readonly postcode: string | null;
  readonly country: string | null;
  readonly displayName: string | null;
}

/** A unit record. */
export interface Unit {
  readonly id: string;
  readonly propertyId: string;
  readonly name: string;
  readonly type: string | null;
  readonly maxOccupancy: number;
  readonly baseRateMinor: number;
  readonly baseRateCurrency: string;
  readonly amenities: readonly string[];
  readonly createdAt: string;
  /** Sellable quantity for this room type (villa/cottage = 1). */
  readonly countOfRooms?: number;
  readonly maxAdults?: number;
  readonly maxChildren?: number;
  readonly maxInfants?: number;
  readonly defaultOccupancy?: number;
  /** Channex room kind — 'room' | 'dorm'. */
  readonly roomKind?: string;
  /** 'active' | 'archived'. */
  readonly status?: string;
  readonly description?: string | null;
}

/** Per-unit Channex sync status shown in the Units list (§17). */
export type UnitSyncStatusValue =
  | "synced"
  | "syncing"
  | "not_connected"
  | "pending"
  | "needs_attention"
  | "sync_failed"
  | "mapping_missing";

/** How a rate plan's rate is produced (mirrors core-api `RateMode`). */
export type RateMode = "manual" | "derived" | "auto";
/** Pricing unit for a rate plan (mirrors core-api `SellMode`). */
export type SellMode = "per_room" | "per_person";

/** A rate-plan record. */
export interface RatePlan {
  readonly id: string;
  readonly unitId: string;
  readonly name: string;
  readonly rateMinor: number;
  readonly rateCurrency: string;
  readonly minStay: number | null;
  readonly maxStay: number | null;
  readonly closedToArrival: boolean;
  readonly closedToDeparture: boolean;
  // Distribution enrichment (migration 053). All optional.
  readonly rateMode?: RateMode;
  readonly sellMode?: SellMode;
  readonly mealType?: string | null;
  readonly cancellationPolicy?: string | null;
  readonly taxSet?: string | null;
  readonly parentRatePlanId?: string | null;
  readonly rateModifier?: number | null;
  /** Soft lifecycle: 'active' | 'archived'. */
  readonly status?: "active" | "archived";
}

/** A single availability day. */
export interface AvailabilityDay {
  readonly id: string;
  readonly unitId: string;
  readonly day: string;
  readonly isAvailable: boolean;
  readonly priceMinor: number | null;
}

// ---------------------------------------------------------------------------
// Reconciliation domain types (M4/M5). Mirror the JSON API response shapes.
//
// IMPORTANT: every *Minor money field arrives as a decimal-integer STRING (not
// a number) because payout amounts can exceed the JS safe-integer range. Keep
// them as strings on the wire; convert with `Number(x)` ONLY when handing a
// value to {@link formatMoney} for display.
// ---------------------------------------------------------------------------

/** Per-state match counts from the reconciliation summary. */
export interface ReconStateCounts {
  readonly matched: number;
  readonly variance: number;
  readonly unmatched: number;
  readonly pending: number;
  readonly disputed: number;
}

/** Recovered-revenue summary (FR-4.8). Money fields are minor-unit strings. */
export interface ReconSummary {
  readonly recoveredMinor: string;
  readonly recoverableMinor: string;
  readonly currency: string;
  readonly counts: ReconStateCounts;
}

/** A distribution channel with its commission/tax/fee model. */
export interface ReconChannel {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  /** Commission in basis points (1500 = 15.00%). */
  readonly commissionBps: number;
  /** Tax in basis points (500 = 5.00%). */
  readonly taxBps: number;
  readonly fixedFeeMinor: string;
  readonly currency: string;
  readonly createdAt: string;
}

/** An expected-payout ledger line (the computed side). */
export interface ReconExpected {
  readonly id: string;
  readonly channelId: string;
  readonly reference: string;
  readonly grossMinor: string;
  readonly commissionMinor: string;
  readonly taxMinor: string;
  readonly feeMinor: string;
  readonly expectedNetMinor: string;
  readonly currency: string;
  readonly settledOn: string | null;
  readonly status: string;
}

/** An ingested actual-payout ledger line (the observed side). */
export interface ReconActual {
  readonly id: string;
  readonly channelId: string;
  readonly reference: string;
  readonly netMinor: string;
  readonly currency: string;
  readonly settledOn: string | null;
  readonly source: string;
}

/** Cited variance evidence against prior channel precedent (FR-4.7). */
export interface ReconEvidence {
  /** How many prior payouts on this channel had this exact delta. */
  readonly count: number;
  /** How many prior payouts on this channel were examined. */
  readonly total: number;
  /** count / total in [0,1] (0 when total is 0). */
  readonly proportion: number;
}

/** A reconciliation match joined for a side-by-side ledger view (FR-4.4/4.5). */
export interface ReconMatch {
  readonly id: string;
  readonly state: "matched" | "variance" | "unmatched" | "pending" | "disputed";
  readonly deltaMinor: string;
  readonly classification: "expected" | "anomalous" | null;
  readonly evidence: ReconEvidence | null;
  readonly isAdjustment: boolean;
  readonly status: string;
  readonly actualId: string;
  readonly actualReference: string | null;
  readonly actualNetMinor: string | null;
  readonly expectedId: string | null;
  readonly expectedReference: string | null;
  readonly expectedNetMinor: string | null;
  readonly currency: string | null;
  readonly createdAt: string;
  /** Enriched booking-centric fields (may be null for orphan actuals). */
  readonly channelName: string | null;
  readonly guestName: string | null;
  readonly grossMinor: string | null;
  readonly commissionMinor: string | null;
  readonly taxMinor: string | null;
}

/** The auto-match money-flow summary (Gross→Commission→Tax→Net→Bank). */
export interface ReconFlowSummary {
  readonly grossMinor: string;
  readonly commissionMinor: string;
  readonly taxMinor: string;
  readonly expectedNetMinor: string;
  readonly bankCreditMinor: string;
  readonly varianceMinor: string;
  readonly transactionCount: number;
  readonly currency: string;
}

/** The counts returned by a reconciliation run. */
export interface ReconRunResult {
  readonly matched: number;
  readonly variance: number;
  readonly unmatched: number;
  readonly pending: number;
}

/** The counts returned by seeding the demo dataset. */
export interface ReconSeedResult {
  readonly channels: number;
  readonly expected: number;
  readonly actual: number;
}

// ---------------------------------------------------------------------------
// Service calls.
// ---------------------------------------------------------------------------

/** Dashboard summary → POST /api/inventory/summary. */
export function apiSummary(): Promise<InventorySummary> {
  return postJson<InventorySummary>("/api/inventory/summary", {});
}

/** List properties → POST /api/inventory/properties/list. */
export async function apiListProperties(): Promise<readonly Property[]> {
  const data = await postJson<{ properties: readonly Property[] }>(
    "/api/inventory/properties/list",
    {},
  );
  return data.properties ?? [];
}

/** Successful create response: the new id + the resulting distribution status. */
export interface CreatePropertyOk {
  readonly id: string;
  readonly distribution: DistributionStatus;
}

/** Successful get/update response: the property + its distribution status. */
export interface PropertyWithDistribution {
  readonly property: Property;
  readonly distribution: DistributionStatus;
}

/**
 * Create a property (full detail + distribution settings) and provision it to
 * Channex → POST /api/inventory/properties/create. The AVOS property is created
 * even if provisioning fails; `distribution.state` reports the outcome.
 *
 * Pass `input.draft === true` to SAVE AS A DRAFT instead: the property is
 * created with `distribution.state === 'draft'` and provisioning is DEFERRED
 * (never pushed to the channel manager) until it is later saved for real.
 */
export function apiCreateProperty(
  input: PropertyFormInput,
): Promise<CreatePropertyOk | ErrorEnvelope> {
  return postJson("/api/inventory/properties/create", input);
}

/** Fetch a single property + its distribution status → /properties/get. */
export function apiGetProperty(
  propertyId: string,
): Promise<PropertyWithDistribution | ErrorEnvelope> {
  return postJson("/api/inventory/properties/get", { propertyId });
}

/** Update a property (detail + distribution) → /properties/update. */
export function apiUpdateProperty(
  propertyId: string,
  input: Partial<PropertyFormInput>,
): Promise<PropertyWithDistribution | ErrorEnvelope> {
  return postJson("/api/inventory/properties/update", { propertyId, ...input });
}

/** Retry Channex provisioning for a property → /properties/provision-retry. */
export function apiRetryProvision(
  propertyId: string,
): Promise<{ distribution: DistributionStatus }> {
  return postJson("/api/inventory/properties/provision-retry", { propertyId });
}

/** List per-property distribution enrichment → /properties/distribution. */
export async function apiPropertyDistribution(): Promise<
  readonly PropertyDistribution[]
> {
  const data = await postJson<{ distribution: readonly PropertyDistribution[] }>(
    "/api/inventory/properties/distribution",
    {},
  );
  return data.distribution ?? [];
}

/** Geocode an address via the backend Nominatim proxy → /inventory/geocode. */
export async function apiGeocode(
  query: string,
): Promise<GeocodeResult | null> {
  const data = await postJson<{ result: GeocodeResult | null; error?: unknown }>(
    "/api/inventory/geocode",
    { query },
  );
  return data.result ?? null;
}

// ---------------------------------------------------------------------------
// Safe Property Removal / Decommissioning (Feature A) — a distinct region added
// without altering the surrounding property services.
// ---------------------------------------------------------------------------

/** The removal mode AVOS can take. */
export type RemovalMode = "hard_delete" | "decommission";

/** One connected OTA channel in the removal impact summary. */
export interface RemovalAssessmentConnection {
  readonly otaCode: string;
  readonly status: string;
  readonly channexChannelId: string | null;
  readonly live: boolean;
}

/** The impact summary + decision for removing a property (backend-computed). */
export interface PropertyRemovalAssessment {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly lifecycleState: string;
  readonly roomCount: number;
  readonly ratePlanCount: number;
  readonly futureReservationCount: number;
  readonly currentlyStayingCount: number;
  readonly historicalReservationCount: number;
  readonly totalReservationCount: number;
  readonly reservationVersionCount: number;
  readonly activeConnections: readonly RemovalAssessmentConnection[];
  readonly channexPropertyStatus: string | null;
  readonly channexPropertyIsReal: boolean;
  readonly pendingSyncJobs: number;
  readonly failedSyncJobs: number;
  readonly unacknowledgedBookingEvents: number;
  readonly needsAttentionConnections: number;
  readonly hasFinancialDependencies: boolean;
  readonly recommendedMode: RemovalMode;
  readonly hardDeleteAllowed: boolean;
  readonly blockers: readonly string[];
}

/** The result of a removal request. */
export interface PropertyRemovalResult {
  readonly propertyId: string;
  readonly lifecycleState: string;
  readonly channexCleanupState: string;
  readonly mode: RemovalMode;
  readonly deleted: boolean;
  readonly decommissioned: boolean;
  readonly manualCleanupRequired: boolean;
  /**
   * True when a `booking_com` (or other extranet-managed OTA) channel was
   * removed at Channex but the operator may still need to disconnect Channex as
   * the connectivity provider in the OTA extranet. Does NOT indicate failure.
   */
  readonly extranetDisconnectRequired: boolean;
  /** Channel names that still require manual cleanup (drives the UI note). */
  readonly manualCleanupChannels: readonly string[];
  /** A human-readable, sanitized post-removal note (null when none). */
  readonly manualCleanupMessage: string | null;
  readonly channelsDisconnected: number;
  readonly outcome: "deleted" | "decommissioned";
}

/**
 * Fetch the removal impact assessment for a property (impact summary +
 * recommended mode + blockers) → POST /api/inventory/properties/removal-assessment.
 * RBAC (`properties.archive`) + tenant are enforced server-side.
 */
export function apiAssessPropertyRemoval(
  propertyId: string,
): Promise<{ assessment: PropertyRemovalAssessment } | ErrorEnvelope> {
  return postJson("/api/inventory/properties/removal-assessment", { propertyId });
}

/**
 * Request removal of a property (orchestrated disconnect → Channex cleanup →
 * delete/decommission) → POST /api/inventory/properties/remove. The actor +
 * tenant + permission are resolved server-side; `confirmName` must match the
 * property name exactly.
 */
export function apiRemoveProperty(input: {
  readonly propertyId: string;
  readonly mode: RemovalMode;
  readonly confirmName: string;
}): Promise<{ removal: PropertyRemovalResult } | ErrorEnvelope> {
  return postJson("/api/inventory/properties/remove", input);
}

// ---------------------------------------------------------------------------
// Property Retirement workflow (scheduled decommission layer, Feature A) — a
// distinct region added for the Danger Zone / Archived tab UI. All actor +
// tenant + RBAC (`properties.archive`) resolution happens server-side. The
// frontend agent consumes these; this region adds NO changes to the surrounding
// property services. The backend endpoints live in api-inventory.mjs.
// ---------------------------------------------------------------------------

/** The removal/retirement CASE the backend decision tree resolves to. */
export type RetirementCase = "canHardDelete" | "canDecommissionNow" | "requiresRetirement";

/** The lightweight retirement marker (separate from lifecycle_state). */
export type RetirementState = "scheduled" | "auto_decommissioning";

/** A single future/current reservation standing between the operator and decommission. */
export interface RetirementReservation {
  readonly reservationId: string;
  readonly guestName: string | null;
  /** The display channel (e.g. "Airbnb", "Direct"). */
  readonly source: string;
  /** The source grouping (direct | ota | manual | walk_in). */
  readonly sourceType: string;
  /** A booking reference (direct confirmation code, else OTA code). */
  readonly reference: string | null;
  readonly unitName: string | null;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly status: string;
  /** True when AVOS can cancel this booking directly (direct source). */
  readonly isDirect: boolean;
}

/** The extended retirement assessment (CASE + lastCheckoutDate + future bookings). */
export interface RetirementAssessment {
  /** The full removal impact summary (counts, blockers, connections). */
  readonly removal: PropertyRemovalAssessment;
  readonly retirementCase: RetirementCase;
  /** The latest checkout across non-cancelled future/current reservations (CASE C). */
  readonly lastCheckoutDate: string | null;
  readonly futureReservations: readonly RetirementReservation[];
  readonly directCancellableCount: number;
  readonly otaCount: number;
  readonly retirementState: RetirementState | null;
  readonly stopNewBookings: boolean;
  readonly scheduledDecommissionAt: string | null;
}

/** The result of scheduling a decommission. */
export interface ScheduleDecommissionResult {
  readonly propertyId: string;
  readonly retirementState: RetirementState;
  readonly scheduledDecommissionAt: string;
  readonly stopNewBookings: boolean;
}

/** The result of cancelling a scheduled decommission. */
export interface CancelScheduledDecommissionResult {
  readonly propertyId: string;
  readonly releasedBlocks: number;
}

/** A per-reservation outcome from resolving direct bookings. */
export interface ResolveDirectBookingOutcome {
  readonly reservationId: string;
  readonly cancelled: boolean;
  readonly note: string | null;
}

/** The result of resolving (cancelling) direct bookings. */
export interface ResolveDirectBookingsResult {
  readonly cancelledCount: number;
  readonly skippedCount: number;
  readonly outcomes: readonly ResolveDirectBookingOutcome[];
}

/** The dry-run preview count before resolving direct bookings. */
export interface ResolveDirectBookingsPreview {
  readonly eligibleDirectCount: number;
  readonly otaCount: number;
}

/** An archived (decommissioned) property row for the Archived tab. */
export interface ArchivedPropertyRow extends Property {
  readonly decommissionedAt: string | null;
}

/** A historical reservation row in the archived property read-only detail. */
export interface ArchivedReservationRow {
  readonly reservationId: string;
  readonly guestName: string | null;
  readonly unitName: string | null;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly status: string;
  readonly source: string;
  readonly channel: string;
  readonly amountMinor: string;
  readonly currency: string;
  readonly confirmationCode: string | null;
  readonly otaReservationCode: string | null;
}

/** The read-only archived property detail (retained history + guests). */
export interface ArchivedPropertyDetail {
  readonly propertyId: string;
  readonly name: string;
  readonly address: string | null;
  readonly city: string | null;
  readonly country: string;
  readonly lifecycleState: string;
  readonly decommissionedAt: string | null;
  readonly units: readonly { readonly unitId: string; readonly name: string | null; readonly status: string }[];
  readonly reservations: readonly ArchivedReservationRow[];
  readonly guests: readonly { readonly guestId: string; readonly name: string | null; readonly email: string | null }[];
  readonly historicalRevenueMinor: string;
  readonly channels: readonly string[];
}

/**
 * Fetch the retirement assessment (CASE A/B/C + lastCheckoutDate + the future
 * booking list) → POST /api/inventory/properties/retirement-assessment.
 */
export function apiRetirementAssessment(
  propertyId: string,
): Promise<{ assessment: RetirementAssessment } | ErrorEnvelope> {
  return postJson("/api/inventory/properties/retirement-assessment", { propertyId });
}

/**
 * Schedule a decommission for a property with future/current reservations →
 * POST /api/inventory/properties/schedule-decommission. `confirmName` must match
 * the property name exactly; `stopNewBookings` optionally halts new bookings now.
 */
export function apiScheduleDecommission(input: {
  readonly propertyId: string;
  readonly confirmName: string;
  readonly stopNewBookings: boolean;
}): Promise<{ scheduled: ScheduleDecommissionResult } | ErrorEnvelope> {
  return postJson("/api/inventory/properties/schedule-decommission", input);
}

/**
 * Cancel a scheduled decommission (while still scheduled + active) →
 * POST /api/inventory/properties/cancel-scheduled-decommission. Releases the
 * stop-new-bookings inventory blocks created by the schedule.
 */
export function apiCancelScheduledDecommission(
  propertyId: string,
): Promise<{ cancelled: CancelScheduledDecommissionResult } | ErrorEnvelope> {
  return postJson("/api/inventory/properties/cancel-scheduled-decommission", { propertyId });
}

/**
 * Resolve direct bookings for a retiring property →
 * POST /api/inventory/properties/resolve-direct-bookings. Pass `preview: true`
 * for the dry-run count ("N direct bookings will be cancelled"); pass
 * `reservationIds` to cancel specific bookings, or omit it to cancel ALL
 * eligible direct future bookings (bulk). OTA bookings are never cancelled.
 */
export function apiResolveDirectBookings(input: {
  readonly propertyId: string;
  readonly preview?: boolean;
  readonly reservationIds?: readonly string[];
}): Promise<
  { preview: ResolveDirectBookingsPreview } | { resolved: ResolveDirectBookingsResult } | ErrorEnvelope
> {
  return postJson("/api/inventory/properties/resolve-direct-bookings", input);
}

/** List archived (decommissioned) properties → /properties/archived/list. */
export async function apiListArchivedProperties(): Promise<readonly ArchivedPropertyRow[]> {
  const data = await postJson<{ properties: readonly ArchivedPropertyRow[] } | ErrorEnvelope>(
    "/api/inventory/properties/archived/list",
    {},
  );
  return "properties" in data ? data.properties : [];
}

/** Fetch an archived property's read-only detail → /properties/archived/detail. */
export function apiArchivedPropertyDetail(
  propertyId: string,
): Promise<{ detail: ArchivedPropertyDetail } | ErrorEnvelope> {
  return postJson("/api/inventory/properties/archived/detail", { propertyId });
}

/** List units → POST /api/inventory/units/list. */
export async function apiListUnits(
  propertyId?: string,
): Promise<readonly Unit[]> {
  const data = await postJson<{ units: readonly Unit[] }>(
    "/api/inventory/units/list",
    propertyId !== undefined ? { propertyId } : {},
  );
  return data.units ?? [];
}

/** The result of a unit create/edit, carrying the resulting Channex sync state. */
export interface UnitMutationResult {
  readonly id: string;
  readonly sync?: { readonly status: string; readonly created?: boolean };
}

/** Create a unit (+ optional initial rate plan) → POST /api/inventory/units/create. */
export function apiCreateUnit(input: {
  propertyId: string;
  name: string;
  type?: string;
  maxOccupancy?: number;
  baseRateMinor?: number;
  baseRateCurrency?: string;
  amenities?: readonly string[];
  countOfRooms?: number;
  maxAdults?: number;
  maxChildren?: number;
  maxInfants?: number;
  defaultOccupancy?: number;
  roomKind?: "room" | "dorm";
  description?: string;
  createRatePlan?: boolean;
  ratePlanName?: string;
  // Optional initial-rate-plan subset (directive H): threaded onto the Standard
  // rate plan created alongside the unit. Wire names match api-inventory.mjs.
  ratePlanSellMode?: SellMode;
  ratePlanMealType?: string | null;
  ratePlanCancellationPolicy?: string | null;
  ratePlanTaxSet?: string | null;
}): Promise<UnitMutationResult | ErrorEnvelope> {
  return postJson<UnitMutationResult | ErrorEnvelope>(
    "/api/inventory/units/create",
    input,
  );
}

/** Fetch a single unit + its Channex room-type mapping → /units/get. */
export function apiGetUnit(unitId: string): Promise<
  { unit: Unit; channexRoomTypeId: string | null } | ErrorEnvelope
> {
  return postJson("/api/inventory/units/get", { unitId });
}

/** Update a unit (partial) → POST /api/inventory/units/update. */
export function apiUpdateUnit(input: {
  unitId: string;
  name?: string;
  type?: string;
  maxOccupancy?: number;
  baseRateMinor?: number;
  baseRateCurrency?: string;
  amenities?: readonly string[];
  countOfRooms?: number;
  maxAdults?: number;
  maxChildren?: number;
  maxInfants?: number;
  defaultOccupancy?: number;
  roomKind?: "room" | "dorm";
  description?: string;
  status?: "active" | "archived";
}): Promise<UnitMutationResult | ErrorEnvelope> {
  return postJson("/api/inventory/units/update", input);
}

/** Archive a unit (inactive + close Channex distribution) → /units/archive. */
export function apiArchiveUnit(
  unitId: string,
): Promise<{ id: string; archived: boolean } | ErrorEnvelope> {
  return postJson("/api/inventory/units/archive", { unitId });
}

/** Permanently delete a safe/unused unit → /units/delete. */
export function apiDeleteUnit(
  unitId: string,
): Promise<{ id: string } | ErrorEnvelope> {
  return postJson("/api/inventory/units/delete", { unitId, confirm: true });
}

/** Retry/force per-unit Channex sync ("Fix sync") → /units/sync. */
export function apiSyncUnit(
  unitId: string,
): Promise<{ unitId: string; status: string } | ErrorEnvelope> {
  return postJson("/api/inventory/units/sync", { unitId });
}

/** Result of a property inventory reconciliation (§14). */
export interface InventoryReconcileSummary {
  readonly propertyId: string;
  readonly notConnected: boolean;
  readonly roomsCreated: number;
  readonly roomsUpdated: number;
  readonly roomsAlreadySynced: number;
  readonly ratePlansCreated: number;
  readonly availabilityDaysSynced: number;
  readonly rateDaysSynced: number;
  readonly failures: readonly { readonly unit: string; readonly reason: string }[];
}

/** Reconcile a property's inventory with Channex → /inventory/reconcile. */
export function apiReconcileInventory(
  propertyId: string,
): Promise<InventoryReconcileSummary | ErrorEnvelope> {
  return postJson("/api/inventory/reconcile", { propertyId });
}

/** List rate plans → POST /api/inventory/rate-plans/list. */
export async function apiListRatePlans(
  unitId?: string,
): Promise<readonly RatePlan[]> {
  const data = await postJson<{ ratePlans: readonly RatePlan[] }>(
    "/api/inventory/rate-plans/list",
    unitId !== undefined ? { unitId } : {},
  );
  return data.ratePlans ?? [];
}

/**
 * The distribution-enrichment subset shared by rate-plan create/update
 * (migration 053). All optional; `null` clears a nullable field.
 */
export interface RatePlanDistributionFields {
  readonly rateMode?: RateMode;
  readonly sellMode?: SellMode;
  readonly mealType?: string | null;
  readonly cancellationPolicy?: string | null;
  readonly taxSet?: string | null;
  readonly parentRatePlanId?: string | null;
  readonly rateModifier?: number | null;
}

/** The core editable rate-plan fields shared by create + update. */
export interface RatePlanEditableFields extends RatePlanDistributionFields {
  readonly name?: string;
  readonly rateMinor?: number;
  readonly rateCurrency?: string;
  readonly minStay?: number;
  readonly maxStay?: number;
  readonly closedToArrival?: boolean;
  readonly closedToDeparture?: boolean;
}

/** Create a rate plan → POST /api/inventory/rate-plans/create. */
export function apiCreateRatePlan(
  input: {
    readonly unitId: string;
    readonly name: string;
  } & Omit<RatePlanEditableFields, "name">,
): Promise<CreateResult> {
  return postJson<CreateResult>("/api/inventory/rate-plans/create", input);
}

/**
 * Update an existing rate plan (partial) → POST /api/inventory/rate-plans/update.
 * Only the provided fields change. A derived rate plan requires a
 * `parentRatePlanId` and cannot be its own parent (the backend rejects both with
 * a VALIDATION error); an unknown id returns NOT_FOUND.
 */
export function apiUpdateRatePlan(
  input: { readonly id: string } & RatePlanEditableFields,
): Promise<CreateResult> {
  return postJson<CreateResult>("/api/inventory/rate-plans/update", input);
}

/**
 * Soft-archive a rate plan (status='archived') → POST
 * /api/inventory/rate-plans/archive. AVOS stays the source of truth — no
 * destructive channel delete is performed. Idempotent; unknown id → NOT_FOUND.
 */
export function apiArchiveRatePlan(
  id: string,
): Promise<{ readonly id: string; readonly archived: boolean } | ErrorEnvelope> {
  return postJson("/api/inventory/rate-plans/archive", { id });
}

/** List availability days → POST /api/inventory/availability/list. */
export async function apiListAvailability(input: {
  unitId: string;
  from: string;
  to: string;
}): Promise<readonly AvailabilityDay[]> {
  const data = await postJson<{ days: readonly AvailabilityDay[] }>(
    "/api/inventory/availability/list",
    input,
  );
  return data.days ?? [];
}

/** Set an availability day → POST /api/inventory/availability/set. */
export function apiSetAvailability(input: {
  unitId: string;
  day: string;
  isAvailable: boolean;
  priceMinor?: number | null;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/inventory/availability/set", input);
}

// ---------------------------------------------------------------------------
// Reconciliation service calls (M4/M5). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// ---------------------------------------------------------------------------

/** Recovered-revenue summary → POST /api/reconciliation/summary. */
export function apiReconSummary(): Promise<ReconSummary> {
  return postJson<ReconSummary>("/api/reconciliation/summary", {});
}

/** Auto-match money-flow summary → POST /api/reconciliation/flow. */
export function apiReconFlow(): Promise<ReconFlowSummary | ErrorEnvelope> {
  return postJson<ReconFlowSummary | ErrorEnvelope>("/api/reconciliation/flow", {});
}

/** List channels → POST /api/reconciliation/channels/list. */
export async function apiListChannels(): Promise<readonly ReconChannel[]> {
  const data = await postJson<{ channels: readonly ReconChannel[] }>(
    "/api/reconciliation/channels/list",
    {},
  );
  return data.channels ?? [];
}

/** List expected payouts → POST /api/reconciliation/expected/list. */
export async function apiListExpected(): Promise<readonly ReconExpected[]> {
  const data = await postJson<{ expected: readonly ReconExpected[] }>(
    "/api/reconciliation/expected/list",
    {},
  );
  return data.expected ?? [];
}

/** List actual payouts → POST /api/reconciliation/actual/list. */
export async function apiListActual(): Promise<readonly ReconActual[]> {
  const data = await postJson<{ actual: readonly ReconActual[] }>(
    "/api/reconciliation/actual/list",
    {},
  );
  return data.actual ?? [];
}

/** List reconciliation matches → POST /api/reconciliation/matches/list. */
export async function apiListMatches(): Promise<readonly ReconMatch[]> {
  const data = await postJson<{ matches: readonly ReconMatch[] }>(
    "/api/reconciliation/matches/list",
    {},
  );
  return data.matches ?? [];
}

/** Run the match engine → POST /api/reconciliation/run. */
export function apiRunReconciliation(): Promise<ReconRunResult | ErrorEnvelope> {
  return postJson<ReconRunResult | ErrorEnvelope>(
    "/api/reconciliation/run",
    {},
  );
}

/** Seed the demo dataset → POST /api/reconciliation/seed-demo. */
export function apiSeedReconDemo(): Promise<ReconSeedResult | ErrorEnvelope> {
  return postJson<ReconSeedResult | ErrorEnvelope>(
    "/api/reconciliation/seed-demo",
    {},
  );
}

/** Close (lock) a match → POST /api/reconciliation/close. */
export function apiCloseMatch(id: string): Promise<CreateResult> {
  return postJson<CreateResult>("/api/reconciliation/close", { id });
}

// ---------------------------------------------------------------------------
// Direct Booking domain types (M2). Mirror the JSON API response shapes.
//
// IMPORTANT: every *Minor money field arrives as a decimal-integer STRING (not
// a number) because reservation amounts are stored as bigint. Keep them as
// strings on the wire; convert with `Number(x)` ONLY when handing a value to
// {@link formatMoney} for display.
// ---------------------------------------------------------------------------

/** How a reservation is paid for (FR-2.3). */
export type PaymentMode = "full_prepayment" | "deposit" | "pay_at_property";

/** The lifecycle state of a reservation. */
export type ReservationStatus =
  | "held"
  | "confirmed"
  | "cancelled"
  | "overbooked";

/** A reservation record. Money fields are minor-unit strings. */
export interface Reservation {
  readonly id: string;
  readonly unitId: string;
  readonly guestId: string;
  /** Check-in date `YYYY-MM-DD`. */
  readonly checkIn: string;
  /** Check-out date `YYYY-MM-DD`, exclusive. */
  readonly checkOut: string;
  readonly status: ReservationStatus;
  readonly amountMinor: string;
  readonly currency: string;
  readonly paymentMode: PaymentMode;
  readonly depositMinor: string;
  readonly paymentTokenRef: string | null;
  readonly channel: string;
  readonly holdExpiresAt: string | null;
  readonly cancelledAt: string | null;
  readonly refundMinor: string | null;
  readonly confirmationCode: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A guest record. */
export interface Guest {
  readonly id: string;
  readonly fullName: string;
  readonly email: string | null;
  readonly phone: string | null;
}

/** The success shape returned by placing a hold (FR-2.2). */
export interface HoldResult {
  readonly id: string;
  readonly amountMinor: string;
  readonly holdExpiresAt: string;
}

/** The success shape returned by confirming a booking (FR-2.4). */
export interface ConfirmResult {
  readonly id: string;
  readonly confirmationCode: string;
}

/** The success shape returned by cancelling a reservation (FR-2.5). */
export interface CancelResult {
  readonly id: string;
  readonly refundMinor: string;
}

/** The success shape returned by sweeping expired holds (FR-2.6). */
export interface ExpireHoldsResult {
  readonly expired: number;
}

// ---------------------------------------------------------------------------
// Direct Booking service calls (M2). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// Success shapes are returned directly; errors arrive as an ErrorEnvelope
// (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** List reservations → POST /api/booking/reservations/list. */
export async function apiListReservations(
  unitId?: string,
): Promise<readonly Reservation[]> {
  const data = await postJson<{ reservations: readonly Reservation[] }>(
    "/api/booking/reservations/list",
    unitId !== undefined ? { unitId } : {},
  );
  return data.reservations ?? [];
}

/** List guests → POST /api/booking/guests/list. */
export async function apiListGuests(): Promise<readonly Guest[]> {
  const data = await postJson<{ guests: readonly Guest[] }>(
    "/api/booking/guests/list",
    {},
  );
  return data.guests ?? [];
}

/** Create a guest → POST /api/booking/guests/create. */
export function apiCreateGuest(input: {
  fullName: string;
  email?: string;
  phone?: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/booking/guests/create", input);
}

/** A guest search result (composer lookup): CRM profile + stay history. */
export interface GuestSearchRow {
  readonly id: string;
  readonly fullName: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly dob: string | null;
  readonly city: string | null;
  readonly country: string | null;
  readonly tags: readonly string[];
  readonly preferences: string | null;
  readonly dietary_notes: string | null;
  readonly stayCount: number;
}

/** A staff member for the Booked By selector (reuses Staff & Payroll). */
export interface StaffOption {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly commissionPct: number;
}

/** A composer add-on / extra charge line. */
export interface ComposerAddon {
  readonly type: string;
  readonly name: string;
  readonly qty: number;
  readonly priceMinor: number;
}

/** A composer split-payment entry. */
export interface ComposerPayment {
  readonly method: string;
  readonly amountMinor: number;
  readonly reference?: string;
  readonly paidOn?: string;
}

/** The live quote (rate + availability + tax) from POST /api/booking/quote. */
export interface QuoteResult {
  readonly currency: string;
  readonly nights: number;
  readonly available: boolean | null;
  readonly occupancy: { readonly maxOccupancy: number; readonly maxAdults: number | null; readonly maxChildren: number | null };
  readonly ratePlan: { readonly id: string; readonly name: string } | null;
  readonly ratePlans: readonly { readonly id: string; readonly name: string; readonly rateMinor: string }[];
  readonly nightlyRateMinor: string;
  // Pricing transparency (explains base vs selected-date rate):
  readonly baseRateMinor?: string;
  readonly resolvedNightlyRateMinor?: string;
  readonly ratesVary?: boolean;
  readonly nightlyBreakdown?: readonly {
    readonly day: string;
    readonly baseRateMinor: string;
    readonly dailyOverrideMinor: string | null;
    readonly overrideSource: string | null;
    readonly finalRateMinor: string;
  }[];
  readonly dynamicPricing?: { readonly baseRateMinor: string; readonly quotedRateMinor: string; readonly appliedRuleCount: number } | null;
  readonly roomsSubtotalMinor: string;
  readonly extrasMinor: string;
  readonly subtotalMinor: string;
  readonly discountMinor: string;
  readonly taxableMinor: string;
  readonly taxMinor: string;
  readonly totalMinor: string;
}

/** The composer confirm/hold result. */
export interface ComposeResult {
  readonly reservationId: string;
  readonly invoiceId: string | null;
  readonly confirmationCode: string | null;
  readonly currency: string;
  readonly paidMinor: string;
  readonly holdExpiresAt: string | null;
  readonly status: "confirmed" | "held";
}

/** Search guests (name/phone/email) → POST /api/booking/guests/search. */
export async function apiSearchGuests(q: string): Promise<readonly GuestSearchRow[]> {
  const data = await postJson<{ guests: readonly GuestSearchRow[] }>(
    "/api/booking/guests/search",
    { q },
  );
  return data.guests ?? [];
}

/** Check for a duplicate guest by phone/email → POST /api/booking/guests/check-duplicate. */
export async function apiCheckDuplicateGuest(input: { phone?: string; email?: string }): Promise<readonly GuestSearchRow[]> {
  const data = await postJson<{ matches: readonly GuestSearchRow[] }>(
    "/api/booking/guests/check-duplicate",
    input,
  );
  return data.matches ?? [];
}

/** Create a full CRM guest → POST /api/booking/guests/create-full. */
export function apiCreateGuestFull(input: Record<string, unknown>): Promise<CreateResult> {
  return postJson<CreateResult>("/api/booking/guests/create-full", input);
}

/** List staff for Booked By → POST /api/booking/staff/list. */
export async function apiListStaff(propertyId?: string): Promise<readonly StaffOption[]> {
  const data = await postJson<{ staff: readonly StaffOption[] }>(
    "/api/booking/staff/list",
    propertyId !== undefined ? { propertyId } : {},
  );
  return data.staff ?? [];
}

/** Live quote (rate/availability/tax) → POST /api/booking/quote. */
export function apiQuote(input: {
  unitId: string;
  checkIn: string;
  checkOut: string;
  ratePlanId?: string;
  overrideRateMinor?: number;
  addons?: readonly ComposerAddon[];
  discountType?: "none" | "percentage" | "fixed";
  discountValue?: number;
}): Promise<QuoteResult | ErrorEnvelope> {
  return postJson<QuoteResult | ErrorEnvelope>("/api/booking/quote", input);
}

/** Compose (hold or confirm) the whole booking → POST /api/booking/compose. */
export function apiCompose(input: Record<string, unknown>): Promise<ComposeResult | ErrorEnvelope> {
  return postJson<ComposeResult | ErrorEnvelope>("/api/booking/compose", input);
}

/** Place a hold on a unit for the given dates → POST /api/booking/hold. */
export function apiHold(input: {
  unitId: string;
  guestId: string;
  checkIn: string;
  checkOut: string;
  nightlyRateMinor: number;
  currency?: string;
  paymentMode?: PaymentMode;
  depositPct?: number;
  channel?: string;
  partySize?: number;
}): Promise<HoldResult | ErrorEnvelope> {
  return postJson<HoldResult | ErrorEnvelope>("/api/booking/hold", input);
}

/** Confirm a held reservation → POST /api/booking/confirm. */
export function apiConfirmBooking(input: {
  reservationId: string;
  paymentTokenRef?: string;
}): Promise<ConfirmResult | ErrorEnvelope> {
  return postJson<ConfirmResult | ErrorEnvelope>(
    "/api/booking/confirm",
    input,
  );
}

/** Cancel a held/confirmed reservation → POST /api/booking/cancel. */
export function apiCancelBooking(input: {
  reservationId: string;
  daysBeforeCheckIn?: number;
  reason?: string;
  note?: string;
  actorName?: string;
}): Promise<CancelResult | ErrorEnvelope> {
  return postJson<CancelResult | ErrorEnvelope>("/api/booking/cancel", input);
}

/** The result of a booking modification (same reservation, new state). */
export interface ModifyBookingResult {
  readonly id: string;
  readonly revision: number;
  readonly unitId: string;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly amountMinor: string;
  readonly previousAmountMinor: string;
  readonly inventoryMoved: boolean;
}

/** Edit a booking IN PLACE (same reservation) → POST /api/booking/modify. */
export function apiModifyBooking(input: {
  reservationId: string;
  expectedRevision?: number;
  unitId?: string;
  checkIn?: string;
  checkOut?: string;
  guestId?: string;
  adults?: number | null;
  children?: number | null;
  infants?: number | null;
  arrivalTime?: string | null;
  bookedById?: string | null;
  specialRequests?: string | null;
  internalNotes?: string | null;
  nightlyRateMinor?: number | null;
  amountMinorOverride?: number | null;
  actorName?: string;
}): Promise<ModifyBookingResult | ErrorEnvelope> {
  return postJson<ModifyBookingResult | ErrorEnvelope>("/api/booking/modify", input);
}

/** A booking history/audit event. */
export interface BookingHistoryEvent {
  readonly id: string;
  readonly reservationId: string;
  readonly eventType: string;
  readonly summary: string;
  readonly detail: Record<string, unknown>;
  readonly actorId: string | null;
  readonly actorName: string | null;
  readonly createdAt: string;
}

/** Load a booking's history timeline → POST /api/booking/history. */
export async function apiBookingHistory(reservationId: string): Promise<readonly BookingHistoryEvent[]> {
  const data = await postJson<{ events?: readonly BookingHistoryEvent[] } | ErrorEnvelope>(
    "/api/booking/history",
    { reservationId },
  );
  return (data as { events?: readonly BookingHistoryEvent[] }).events ?? [];
}

/** Sweep expired holds → POST /api/booking/expire-holds. */
export function apiExpireHolds(): Promise<ExpireHoldsResult | ErrorEnvelope> {
  return postJson<ExpireHoldsResult | ErrorEnvelope>(
    "/api/booking/expire-holds",
    {},
  );
}

// ---------------------------------------------------------------------------
// Bookings register (M2) — the aggregated, filterable reservations view (both
// direct and channel-ingested). Money fields are minor-unit STRINGS.
// ---------------------------------------------------------------------------

/** Derived payment status of a booking. */
export type PaymentStatus =
  | "paid"
  | "deposit_paid"
  | "pay_at_property"
  | "pending"
  | "refunded"
  | "cancelled";

/** A booking row for the register (joined + derived). */
export interface BookingRow {
  readonly id: string;
  readonly guestId: string;
  readonly guestName: string | null;
  readonly guestEmail: string | null;
  readonly unitId: string;
  readonly unitName: string | null;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly nights: number;
  readonly partySize: number | null;
  readonly channel: string;
  readonly status: ReservationStatus;
  readonly paymentStatus: PaymentStatus;
  readonly amountMinor: string;
  readonly depositMinor: string;
  readonly refundMinor: string | null;
  /**
   * The OTA-provided commission on this booking, in minor-unit STRING form,
   * when the channel reports it; null/absent for direct or unreported bookings.
   * Used by the Channels screen to compute actual (not hardcoded) effective
   * commission per channel.
   */
  readonly otaCommissionMinor?: string | null;
  readonly currency: string;
  readonly confirmationCode: string | null;
  readonly createdAt: string | null;
  /** Fine-grained booking origin: direct | walk_in | manual | airbnb | booking_com | … */
  readonly source?: string;
  /** Coarse grouping: direct | ota | manual | walk_in. */
  readonly sourceType?: string;
  /** Normalized channel sync state: not_applicable | pending | synced | conflict | mapping_required | failed. */
  readonly channelSyncStatus?: string;
  readonly channelLastSyncedAt?: string | null;
  /** OTA-side identifiers (present only for OTA reservations). */
  readonly otaReservationCode?: string | null;
  readonly externalUniqueId?: string | null;
  readonly channexBookingId?: string | null;
  readonly channexRevisionId?: string | null;
  readonly providerCode?: string | null;
  readonly adults?: number | null;
  readonly children?: number | null;
  readonly infants?: number | null;
  readonly arrivalTime?: string | null;
  /** Cancellation reason (present only for cancelled bookings). */
  readonly cancellationReason?: string | null;
}

/** Summary counts accompanying the register. */
export interface BookingsSummary {
  readonly total: number;
  readonly confirmed: number;
  readonly held: number;
  readonly cancelled: number;
  readonly upcoming: number;
  readonly confirmedRevenueMinor: string;
  readonly currency: string;
}

/** The register response: rows + summary. */
export interface BookingsResult {
  readonly rows: readonly BookingRow[];
  readonly summary: BookingsSummary;
}

/** Filters for {@link apiListBookings}. */
export interface BookingsFilter {
  readonly status?: ReservationStatus;
  readonly channel?: string;
  readonly from?: string;
  readonly to?: string;
  readonly search?: string;
  readonly limit?: number;
}

/** The bookings register → POST /api/booking/bookings/list. */
export async function apiListBookings(
  filter: BookingsFilter = {},
): Promise<BookingsResult> {
  const data = await postJson<BookingsResult>(
    "/api/booking/bookings/list",
    filter,
  );
  return {
    rows: data.rows ?? [],
    summary:
      data.summary ?? {
        total: 0,
        confirmed: 0,
        held: 0,
        cancelled: 0,
        upcoming: 0,
        confirmedRevenueMinor: "0",
        currency: "INR",
      },
  };
}

/** Distinct channel labels → POST /api/booking/channels/list. */
export async function apiBookingChannels(): Promise<readonly string[]> {
  const data = await postJson<{ channels: readonly string[] }>(
    "/api/booking/channels/list",
    {},
  );
  return data.channels ?? [];
}

// ---------------------------------------------------------------------------
// Channel Manager domain types (M3). Mirror the JSON API response shapes.
//
// IMPORTANT: every *Minor money field arrives as a decimal-integer STRING (not
// a number). Keep them as strings on the wire; convert with `Number(x)` ONLY
// when handing a value to {@link formatMoney} for display.
// ---------------------------------------------------------------------------

/** The connection health of an OTA channel (FR-3.4). */
export type ChannelConnectionStatus = "connected" | "disconnected" | "error";

/** A channel connection record with its live sync health. */
export interface ChannelConnection {
  readonly id: string;
  readonly channelId: string;
  readonly channelName: string;
  readonly channelKind: string;
  readonly status: ChannelConnectionStatus;
  /** ISO timestamp of the last successful sync, or null if never synced. */
  readonly lastSyncAt: string | null;
  readonly errorCount: number;
  readonly pendingOps: number;
  readonly lastError: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

/** Which side wins a conflict (§5.3): OTA for reservations, AVOS for rates. */
export type ConflictAuthority = "ota" | "avos";

/** A logged channel conflict — overbookings and rate-parity breaches (FR-3.6). */
export interface ChannelConflict {
  readonly id: string;
  readonly channelId: string;
  readonly kind: string;
  readonly detail: string;
  readonly resolution: string;
  readonly authority: ConflictAuthority;
  /** ISO timestamp of when the conflict occurred. */
  readonly occurredAt: string | null;
}

/** The tally returned by syncing a channel (FR-3.4). */
export interface SyncResult {
  readonly pulled: number;
  readonly created: number;
  readonly updated: number;
  readonly discarded: number;
  /** Overlapping OTA reservations accepted and flagged, never dropped (§5.2). */
  readonly overbooked: number;
}

/** The result of a rate-parity check (FR-3.5). Money fields are minor strings. */
export interface RateParityResult {
  readonly breach: boolean;
  readonly deltaMinor: string;
}

/** The counts returned by seeding the channel demo dataset. */
export interface ChannelSeedResult {
  readonly channels: number;
  readonly connected: number;
  readonly queuedInbound: number;
}

// ---------------------------------------------------------------------------
// Channel Manager service calls (M3). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// Success shapes are returned directly; errors arrive as an ErrorEnvelope
// (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** List channel connections → POST /api/channels/connections/list. */
export async function apiListConnections(): Promise<
  readonly ChannelConnection[]
> {
  const data = await postJson<{ connections: readonly ChannelConnection[] }>(
    "/api/channels/connections/list",
    {},
  );
  return data.connections ?? [];
}

/** Connect a channel → POST /api/channels/connect. */
export function apiConnectChannel(input: {
  channelId: string;
  credentialsRef?: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/channels/connect", input);
}

/** Disconnect a channel → POST /api/channels/disconnect. */
export function apiDisconnectChannel(input: {
  channelId: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/channels/disconnect", input);
}

/** List conflicts → POST /api/channels/conflicts/list. */
export async function apiListConflicts(
  channelId?: string,
): Promise<readonly ChannelConflict[]> {
  const data = await postJson<{ conflicts: readonly ChannelConflict[] }>(
    "/api/channels/conflicts/list",
    channelId !== undefined ? { channelId } : {},
  );
  return data.conflicts ?? [];
}

/** Sync a channel → POST /api/channels/sync. */
export function apiSyncChannel(
  channelId: string,
): Promise<SyncResult | ErrorEnvelope> {
  return postJson<SyncResult | ErrorEnvelope>("/api/channels/sync", {
    channelId,
  });
}

/** Check rate parity → POST /api/channels/rate-parity. */
export function apiRateParity(input: {
  channelId: string;
  avosRateMinor: string;
  channelRateMinor: string;
}): Promise<RateParityResult | ErrorEnvelope> {
  return postJson<RateParityResult | ErrorEnvelope>(
    "/api/channels/rate-parity",
    input,
  );
}

/** Seed the channel demo dataset → POST /api/channels/seed-demo. */
export function apiSeedChannelDemo(): Promise<
  ChannelSeedResult | ErrorEnvelope
> {
  return postJson<ChannelSeedResult | ErrorEnvelope>(
    "/api/channels/seed-demo",
    {},
  );
}

// ---------------------------------------------------------------------------
// Analytics domain types (M9). Mirror the JSON API response shapes.
//
// IMPORTANT: every *Minor money field arrives as a decimal-integer STRING (not
// a number) because revenue amounts can exceed the JS safe-integer range. Keep
// them as strings on the wire; convert with `Number(x)` ONLY when handing a
// value to {@link formatMoney} for display. Ratios (occupancy, share, pct
// change) are dimensionless and arrive as plain numbers.
// ---------------------------------------------------------------------------

/** Period-over-period comparison figures (FR-10.x). Ratios, not money. */
export interface DashboardComparison {
  /** Proportional change in gross revenue vs the previous period, or null. */
  readonly grossPctChange: number | null;
  /** Proportional change in net revenue vs the previous period, or null. */
  readonly netPctChange: number | null;
  /** Proportional change in RevPAR vs the previous period, or null. */
  readonly revparPctChange: number | null;
}

/** Live dashboard KPI metrics (FR-10.1/10.4). Money fields are minor strings. */
export interface DashboardMetrics {
  /** Sum of gross revenue over confirmed reservations (minor units, string). */
  readonly grossRevenueMinor: string;
  /** Net revenue after commission/tax/fees (FR-10.4) (minor units, string). */
  readonly netRevenueMinor: string;
  /** Sum of nights over confirmed reservations. */
  readonly occupiedRoomNights: number;
  /** Capacity available to sell in the window (unitCount * periodDays). */
  readonly availableRoomNights: number;
  /** Average Daily Rate (minor units, string). */
  readonly adrMinor: string;
  /** Occupancy ratio in `[0, 1]`. */
  readonly occupancy: number;
  /** Revenue Per Available Room (minor units, string). */
  readonly revparMinor: string;
  /** Count of confirmed reservations in the period. */
  readonly reservationCount: number;
  /** Count of bookable units for the tenant. */
  readonly unitCount: number;
  /** ISO 4217 currency the money amounts are denominated in. */
  readonly currency: string;
  /** Period-over-period comparison, or null when no previous window given. */
  readonly comparison: DashboardComparison | null;
}

/** A single channel-mix row (FR-10.2). Money fields are minor strings. */
export interface ChannelMixRow {
  /** The channel label (e.g. `Direct`, `Airbnb`). */
  readonly channel: string;
  /** Sum of nights on this channel. */
  readonly nights: number;
  /** Sum of revenue on this channel (minor units, string). */
  readonly revenueMinor: string;
  /** Count of confirmed reservations on this channel. */
  readonly reservationCount: number;
  /** This channel's revenue / total confirmed revenue, in `[0, 1]`. */
  readonly revenueSharePct: number;
}

/** A single booking-pace day (FR-10.3). Money fields are minor strings. */
export interface PaceRow {
  /** The calendar day, `YYYY-MM-DD`. */
  readonly day: string;
  /** Count of confirmed reservations created that day. */
  readonly reservationCount: number;
  /** Sum of revenue created that day (minor units, string). */
  readonly revenueMinor: string;
}

// ---------------------------------------------------------------------------
// Analytics service calls (M9). Each mirrors the existing style: POST JSON,
// return the typed shape, unwrap the list envelope where present.
// ---------------------------------------------------------------------------

/** Live dashboard metrics → POST /api/analytics/dashboard. */
export function apiDashboardMetrics(input?: {
  from?: string;
  to?: string;
  prevFrom?: string;
  prevTo?: string;
}): Promise<DashboardMetrics | ErrorEnvelope> {
  return postJson<DashboardMetrics | ErrorEnvelope>(
    "/api/analytics/dashboard",
    input ?? {},
  );
}

/** Channel mix → POST /api/analytics/channel-mix. */
export async function apiChannelMix(input?: {
  from?: string;
  to?: string;
}): Promise<readonly ChannelMixRow[]> {
  const data = await postJson<{ mix: readonly ChannelMixRow[] }>(
    "/api/analytics/channel-mix",
    input ?? {},
  );
  return data.mix ?? [];
}

/** Booking pace → POST /api/analytics/pace. */
export async function apiBookingPace(
  days?: number,
): Promise<readonly PaceRow[]> {
  const data = await postJson<{ pace: readonly PaceRow[] }>(
    "/api/analytics/pace",
    days !== undefined ? { days } : {},
  );
  return data.pace ?? [];
}

// ---------------------------------------------------------------------------
// Dashboard extras (M9): revenue trend, upcoming check-ins, property
// performance, and a live rule-based insight. Money fields are minor-unit
// STRINGS on the wire.
// ---------------------------------------------------------------------------

/** One month's year-over-year revenue point. Money as minor-unit strings. */
export interface RevenueTrendPoint {
  readonly month: number;
  readonly label: string;
  readonly thisYearMinor: string;
  readonly lastYearMinor: string;
}

/** An upcoming confirmed check-in (joined guest + property). */
export interface UpcomingCheckIn {
  readonly reservationId: string;
  readonly guestName: string | null;
  readonly propertyName: string | null;
  readonly unitName: string | null;
  readonly channel: string;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly amountMinor: string;
  readonly currency: string;
}

/** Per-property performance row. Money as string, occupancy as ratio. */
export interface PropertyPerformanceRow {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly city: string | null;
  readonly unitCount: number;
  readonly revenueMinor: string;
  readonly occupancy: number;
  readonly currency: string;
}

/** A live, rule-based dashboard insight. */
export interface DashboardInsight {
  readonly tone: "attention" | "ok" | "critical" | "pending";
  readonly headline: string;
  readonly detail: string;
  readonly actionPath: string | null;
  readonly actionLabel: string | null;
}

/** Monthly revenue trend (this year vs last) → POST /api/analytics/revenue-trend. */
export async function apiRevenueTrend(): Promise<readonly RevenueTrendPoint[]> {
  const data = await postJson<{ trend: readonly RevenueTrendPoint[] }>(
    "/api/analytics/revenue-trend",
    {},
  );
  return data.trend ?? [];
}

/** Upcoming check-ins → POST /api/analytics/upcoming-check-ins. */
export async function apiUpcomingCheckIns(
  limit?: number,
): Promise<readonly UpcomingCheckIn[]> {
  const data = await postJson<{ checkIns: readonly UpcomingCheckIn[] }>(
    "/api/analytics/upcoming-check-ins",
    limit !== undefined ? { limit } : {},
  );
  return data.checkIns ?? [];
}

/** Per-property performance → POST /api/analytics/property-performance. */
export async function apiPropertyPerformance(): Promise<
  readonly PropertyPerformanceRow[]
> {
  const data = await postJson<{ properties: readonly PropertyPerformanceRow[] }>(
    "/api/analytics/property-performance",
    {},
  );
  return data.properties ?? [];
}

/** Live rule-based insight → POST /api/analytics/insight. */
export function apiDashboardInsight(): Promise<DashboardInsight | ErrorEnvelope> {
  return postJson<DashboardInsight | ErrorEnvelope>("/api/analytics/insight", {});
}

// ---------------------------------------------------------------------------
// Formatting helpers (pure, SSR-safe).
// ---------------------------------------------------------------------------

/**
 * Format an integer MINOR-unit amount as a localized currency string.
 * e.g. formatMoney(1200000, "INR") -> "₹12,000.00".
 */
export function formatMoney(minor: number, currency: string): string {
  const major = minor / 100;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
    }).format(major);
  } catch {
    // Unknown currency code: fall back to a plain formatted number + code.
    return `${major.toFixed(2)} ${currency}`;
  }
}

/**
 * The bare currency symbol/prefix for compact display, derived from the locale
 * formatter (so it tracks whatever `formatMoney` would show). PURE + SSR-safe.
 * Falls back to a hardcoded map, then to the ISO code + space.
 */
function currencySymbol(currency: string): string {
  const KNOWN: Record<string, string> = {
    INR: "₹",
    GBP: "£",
    EUR: "€",
    USD: "$",
    AUD: "$",
    CAD: "$",
    SGD: "$",
    JPY: "¥",
    AED: "AED ",
  };
  if (KNOWN[currency] !== undefined) return KNOWN[currency];
  try {
    // Extract the symbol Intl would use (locale-independent enough for a prefix).
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === "currency")?.value;
    if (sym !== undefined && sym !== "") return sym;
  } catch {
    /* fall through */
  }
  return `${currency} `;
}

/**
 * Format an integer MINOR-unit amount as a COMPACT currency string for summary /
 * KPI cards, PURE + SSR-safe. INR uses the Indian K / L / Cr scale; every other
 * currency uses the international K / M / B scale. The sign is preserved,
 * values are trimmed to at most 2 decimals with trailing zeroes removed, and a
 * zero renders as a clean `<symbol>0` (never `₹0.00K`). The compact form never
 * wraps; the exact value comes from {@link formatMoney}.
 *
 *   INR:  ₹820 · ₹28.24K · ₹5.46L · ₹1.25Cr
 *   GBP:  £1.25K · £1.25M · £1.2B
 */
export function formatMoneyCompact(minor: number, currency: string): string {
  const major = minor / 100;
  const sign = major < 0 ? "-" : "";
  const abs = Math.abs(major);
  const symbol = currencySymbol(currency);
  // Trim to <=2 decimals, drop trailing zeroes (5.40 → 5.4, 5.00 → 5).
  const trim = (n: number): string => n.toFixed(2).replace(/\.?0+$/, "");
  // Clean zero.
  if (abs === 0) return `${symbol}0`;
  // Below one thousand: show the full grouped amount (locale grouping).
  if (abs < 1_000) {
    const grouped = (() => {
      try {
        return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en", {
          maximumFractionDigits: 2,
        }).format(abs);
      } catch {
        return Number.isInteger(abs) ? String(abs) : trim(abs);
      }
    })();
    return `${sign}${symbol}${grouped}`;
  }
  if (currency === "INR") {
    if (abs < 1_00_000) return `${sign}${symbol}${trim(abs / 1_000)}K`;
    if (abs < 1_00_00_000) return `${sign}${symbol}${trim(abs / 1_00_000)}L`;
    return `${sign}${symbol}${trim(abs / 1_00_00_000)}Cr`;
  }
  // International scale (K / M / B) for non-INR currencies.
  if (abs < 1_000_000) return `${sign}${symbol}${trim(abs / 1_000)}K`;
  if (abs < 1_000_000_000) return `${sign}${symbol}${trim(abs / 1_000_000)}M`;
  return `${sign}${symbol}${trim(abs / 1_000_000_000)}B`;
}

/**
 * Parse a decimal major-unit string (e.g. "12000.50") into integer minor units
 * (1200050). Returns null when the input is not a valid non-negative number.
 */
export function parseMoneyToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) {
    return null;
  }
  return Math.round(num * 100);
}

// ---------------------------------------------------------------------------
// Guest CRM domain types (M6). Mirror the JSON API response shapes.
//
// IMPORTANT: every *Minor money field arrives as a decimal-integer STRING (not
// a number) because lifetime-value amounts are summed from bigint reservation
// amounts and can exceed the JS safe-integer range. Keep them as strings on
// the wire; convert with `Number(x)` ONLY when handing a value to
// {@link formatMoney} for display. Timestamps arrive as ISO strings (or null).
// Success shapes are returned directly; errors arrive as an ErrorEnvelope
// (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** A guest list-row summary with computed lifetime value + stay count (FR-6.1). */
export interface CrmGuestSummary {
  readonly id: string;
  readonly fullName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly tags: readonly string[];
  readonly marketingOptOut: boolean;
  readonly mergedInto: string | null;
  readonly lifetimeValueMinor: string;
  readonly stayCount: number;
  /** Origin booking source recorded at creation (Feature B), or null if unknown. */
  readonly firstSource: string | null;
  /** Most recent booking source (Feature B), or null if unknown. */
  readonly latestSource: string | null;
}

/**
 * A guest enriched with CRM insights DERIVED from existing confirmed
 * reservations (last/next/first stay, source channels, properties visited,
 * lifetime + average booking value). All *Minor fields are decimal-integer
 * STRINGS; dates are `YYYY-MM-DD` or null; there are no birthday/anniversary
 * fields (the schema does not store them). Powers the actionable Guest CRM
 * KPI cards, engagement opportunities, directory and segments.
 */
export interface CrmGuestInsight {
  readonly id: string;
  readonly fullName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly tags: readonly string[];
  readonly marketingOptOut: boolean;
  readonly lifetimeValueMinor: string;
  readonly stayCount: number;
  readonly avgBookingValueMinor: string;
  readonly firstStayDate: string | null;
  readonly lastStayDate: string | null;
  readonly lastCheckoutDate: string | null;
  readonly nextStayDate: string | null;
  readonly currency: string;
  readonly propertiesVisited: readonly string[];
  readonly sources: readonly string[];
}

/**
 * One confirmed reservation ("stay") with its guest and unit/property context,
 * derived from existing reservation data. Powers the "Currently staying" and
 * "Recent guests" overview sections (the client compares `checkIn`/`checkOut`
 * to today). Money is a decimal-integer STRING; dates are `YYYY-MM-DD`.
 */
export interface CrmGuestStay {
  readonly reservationId: string;
  readonly guestId: string;
  readonly guestName: string | null;
  readonly guestEmail: string | null;
  readonly guestPhone: string | null;
  readonly guestTags: readonly string[];
  readonly guestMarketingOptOut: boolean;
  readonly guestPreferences: string | null;
  readonly guestDietaryNotes: string | null;
  readonly guestStayCount: number;
  readonly guestLifetimeValueMinor: string;
  readonly propertyName: string | null;
  readonly propertyDecommissioned: boolean;
  readonly unitName: string | null;
  readonly checkIn: string;
  readonly checkOut: string;
  readonly nights: number;
  readonly channel: string | null;
  readonly source: string | null;
  readonly amountMinor: string;
  readonly currency: string;
}

/** A full guest profile with preferences + dietary notes (FR-6.1). */
export interface CrmGuestProfile {
  readonly id: string;
  readonly fullName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly preferences: string | null;
  readonly dietaryNotes: string | null;
  readonly tags: readonly string[];
  readonly marketingOptOut: boolean;
  readonly mergedInto: string | null;
  readonly lifetimeValueMinor: string;
  readonly stayCount: number;
  /** Origin booking source recorded at creation (Feature B), or null if unknown. */
  readonly firstSource: string | null;
  /** Most recent booking source (Feature B), or null if unknown. */
  readonly latestSource: string | null;
  readonly createdAt: string;
}

/** How confident a duplicate match is (FR-6.2). */
export type DuplicateClassification = "definite" | "probable";

/** A candidate duplicate pair with its score + classification (FR-6.2). */
export interface DuplicatePair {
  readonly guestIdA: string;
  readonly guestIdB: string;
  readonly nameA: string | null;
  readonly nameB: string | null;
  /** Similarity score in `[0, 1]`. */
  readonly score: number;
  readonly classification: DuplicateClassification;
}

/** A message thread joined with the guest name + message count (FR-6.3). */
export interface CrmThread {
  readonly id: string;
  readonly guestId: string;
  readonly guestName: string | null;
  readonly channel: string;
  readonly subject: string | null;
  /** ISO timestamp of the last message, or null when the thread has none. */
  readonly lastMessageAt: string | null;
  readonly messageCount: number;
  readonly createdAt: string;
}

/** Which way a message flows relative to the property. */
export type MessageDirection = "inbound" | "outbound";

/** Who authored a message. */
export type MessageAuthor = "guest" | "operator" | "agent";

/** A single message within a thread (FR-6.3). */
export interface CrmMessage {
  readonly id: string;
  readonly threadId: string;
  readonly direction: MessageDirection;
  readonly kind: string;
  readonly author: MessageAuthor;
  readonly body: string;
  /** ISO timestamp of when the message was sent. */
  readonly sentAt: string;
}

/** The lifecycle-message kinds (FR-6.7); transactional, so bypass opt-out. */
export type LifecycleKind =
  | "confirmation"
  | "pre_arrival"
  | "in_stay"
  | "post_stay"
  | "review_request";

// ---------------------------------------------------------------------------
// Guest CRM service calls (M6). Each mirrors the existing style: POST JSON,
// return the typed shape, unwrap the list envelope where present.
// ---------------------------------------------------------------------------

/** List guests → POST /api/crm/guests/list. */
export async function apiCrmListGuests(): Promise<readonly CrmGuestSummary[]> {
  const data = await postJson<{ guests: readonly CrmGuestSummary[] }>(
    "/api/crm/guests/list",
    {},
  );
  return data.guests ?? [];
}

/** List guests with derived CRM insights → POST /api/crm/guests/insights. */
export async function apiCrmGuestInsights(): Promise<readonly CrmGuestInsight[]> {
  const data = await postJson<{ guests: readonly CrmGuestInsight[] }>(
    "/api/crm/guests/insights",
    {},
  );
  return data.guests ?? [];
}

/** List confirmed stays with guest + unit/property context → POST /api/crm/guests/stays. */
export async function apiCrmGuestStays(): Promise<readonly CrmGuestStay[]> {
  const data = await postJson<{ stays: readonly CrmGuestStay[] }>(
    "/api/crm/guests/stays",
    {},
  );
  return data.stays ?? [];
}

/** Get one guest profile → POST /api/crm/guests/get. */
export function apiCrmGetGuest(
  id: string,
): Promise<CrmGuestProfile | ErrorEnvelope> {
  return postJson<CrmGuestProfile | ErrorEnvelope>("/api/crm/guests/get", {
    id,
  });
}

/** Create a guest → POST /api/crm/guests/create. */
export function apiCrmCreateGuest(input: {
  fullName?: string;
  email?: string;
  phone?: string;
  preferences?: string;
  dietaryNotes?: string;
  tags?: readonly string[];
  marketingOptOut?: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/crm/guests/create", input);
}

/** Set a guest's tags → POST /api/crm/guests/tags. */
export function apiCrmSetTags(input: {
  guestId: string;
  tags: readonly string[];
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/crm/guests/tags", input);
}

/** Set a guest's marketing opt-out → POST /api/crm/guests/opt-out. */
export function apiCrmSetOptOut(input: {
  guestId: string;
  marketingOptOut: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/crm/guests/opt-out", input);
}

/** Find candidate duplicate guest pairs → POST /api/crm/duplicates. */
export async function apiCrmDuplicates(): Promise<readonly DuplicatePair[]> {
  const data = await postJson<{ pairs: readonly DuplicatePair[] }>(
    "/api/crm/duplicates",
    {},
  );
  return data.pairs ?? [];
}

/** Merge two guests (survivor keeps the record) → POST /api/crm/merge. */
export function apiCrmMerge(input: {
  survivorId: string;
  mergedId: string;
}): Promise<{ readonly survivorId: string } | ErrorEnvelope> {
  return postJson<{ readonly survivorId: string } | ErrorEnvelope>(
    "/api/crm/merge",
    input,
  );
}

/**
 * List message threads → POST /api/crm/threads. Omit `guestId` for the unified
 * inbox across every guest; pass one to restrict to that guest.
 */
export async function apiCrmThreads(
  guestId?: string,
): Promise<readonly CrmThread[]> {
  const data = await postJson<{ threads: readonly CrmThread[] }>(
    "/api/crm/threads",
    guestId !== undefined ? { guestId } : {},
  );
  return data.threads ?? [];
}

/** List messages in a thread → POST /api/crm/messages. */
export async function apiCrmMessages(
  threadId: string,
): Promise<readonly CrmMessage[]> {
  const data = await postJson<{ messages: readonly CrmMessage[] }>(
    "/api/crm/messages",
    { threadId },
  );
  return data.messages ?? [];
}

/** Ensure a thread exists for a guest+channel → POST /api/crm/thread/ensure. */
export function apiCrmEnsureThread(input: {
  guestId: string;
  channel: string;
  subject?: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/crm/thread/ensure", input);
}

/**
 * Post a message to a thread → POST /api/crm/message/post. An OUTBOUND
 * `kind='marketing'` message to an opted-out guest is refused with the
 * `OPTED_OUT` error code (FR-6.10); freeform/lifecycle kinds are transactional.
 */
export function apiCrmPostMessage(input: {
  threadId: string;
  direction: MessageDirection;
  body: string;
  kind?: string;
  author?: MessageAuthor;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/crm/message/post", input);
}

/**
 * Send a transactional lifecycle message → POST /api/crm/lifecycle. Lifecycle
 * messages are transactional (not marketing), so they always bypass the
 * marketing opt-out (FR-6.7/6.10).
 */
export function apiCrmLifecycle(input: {
  guestId: string;
  channel: string;
  kind: LifecycleKind;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/crm/lifecycle", input);
}

// ---------------------------------------------------------------------------
// AI Agent Layer domain types (M7). Mirror the JSON API response shapes.
//
// This module surfaces the SRS §9 agent governance: per-agent autonomy, the
// tenant-wide kill switch, confidence thresholds, structural tool prohibitions,
// the live dispatch eval harness, the prompt-injection screener, and the
// immutable action log. Timestamps arrive as ISO strings (or null). Success
// shapes are returned directly; errors arrive as an ErrorEnvelope (narrow with
// {@link asError}).
// ---------------------------------------------------------------------------

/** An agent's autonomy level (§9.1): draft-only, human-supervised, or auto. */
export type AutonomyLevel = "draft" | "supervised" | "auto";

/** The decision a dispatch resolves to (§9.5). */
export type AgentDecision = "acted" | "drafted" | "escalated" | "refused";

/** A persisted per-agent governance config row (FR-AI.4). */
export interface AgentConfig {
  readonly agent: string;
  readonly autonomyLevel: AutonomyLevel;
  readonly enabled: boolean;
  readonly killSwitch: boolean;
  /** Tenant confidence threshold (0..100) below which actions escalate. */
  readonly confidenceThreshold: number;
  readonly updatedAt: string | null;
}

/** The effective governance state resolved for an agent (defaults applied). */
export interface AgentGovernanceState {
  readonly enabled: boolean;
  readonly autonomyLevel: AutonomyLevel;
  readonly killSwitch: boolean;
  readonly confidenceThreshold: number;
}

/** A static agent-catalogue definition exposed to the UI (§9.1/§9.3). */
export interface AgentCatalogueEntry {
  readonly name: string;
  /** The DELIBERATELY LIMITED registered tool list — prohibitions are absence. */
  readonly tools: readonly string[];
  /** The agent's own confidence floor (0..100) below which it escalates. */
  readonly maxConfidenceToAct: number;
}

/** A single immutable action-log entry (FR-AI.1). */
export interface AgentAction {
  readonly id: string;
  readonly agent: string;
  readonly requestedAction: string;
  readonly decision: AgentDecision;
  readonly reason: string;
  readonly inputs: unknown;
  readonly reasoningSummary: string | null;
  readonly output: string | null;
  readonly confidence: number;
  readonly modelVersion: string | null;
  readonly guestContent: boolean;
  readonly createdAt: string | null;
}

/** The success shape returned by dispatching an action (§9.5). */
export interface DispatchResult {
  readonly decision: AgentDecision;
  readonly reason: string;
  readonly actionId: string;
}

/** The result of screening text for prompt injection (§9.4/FR-AI.7). */
export interface InjectionScreen {
  /** Whether the text tripped an injection heuristic. */
  readonly flagged: boolean;
  /** The text wrapped as data (delimited), never as instructions. */
  readonly sanitized: string;
}

/** The combined configs + effective states + catalogue from the API. */
export interface AgentConfigs {
  readonly configs: readonly AgentConfig[];
  readonly states: Readonly<Record<string, AgentGovernanceState>>;
  readonly agents: readonly AgentCatalogueEntry[];
}

// ---------------------------------------------------------------------------
// AI Agent Layer service calls (M7). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// Success shapes are returned directly; errors arrive as an ErrorEnvelope
// (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** Configs + effective states + catalogue → POST /api/agent/configs. */
export async function apiAgentConfigs(): Promise<AgentConfigs> {
  const data = await postJson<{
    configs?: readonly AgentConfig[];
    states?: Readonly<Record<string, AgentGovernanceState>>;
    agents?: readonly AgentCatalogueEntry[];
  }>("/api/agent/configs", {});
  return {
    configs: data.configs ?? [],
    states: data.states ?? {},
    agents: data.agents ?? [],
  };
}

/** Set an agent's autonomy (and optionally enabled) → POST /api/agent/autonomy. */
export function apiAgentSetAutonomy(input: {
  agent: string;
  autonomyLevel: AutonomyLevel;
  enabled?: boolean;
}): Promise<{ agent: string } | ErrorEnvelope> {
  return postJson<{ agent: string } | ErrorEnvelope>(
    "/api/agent/autonomy",
    input,
  );
}

/** Toggle the tenant-wide kill switch → POST /api/agent/kill-switch. */
export function apiAgentKillSwitch(
  on: boolean,
): Promise<{ on: boolean } | ErrorEnvelope> {
  return postJson<{ on: boolean } | ErrorEnvelope>("/api/agent/kill-switch", {
    on,
  });
}

/** Dispatch an action through the guardrails → POST /api/agent/dispatch. */
export function apiAgentDispatch(input: {
  agent: string;
  action: string;
  confidence: number;
  containsGuestContent?: boolean;
}): Promise<DispatchResult | ErrorEnvelope> {
  return postJson<DispatchResult | ErrorEnvelope>(
    "/api/agent/dispatch",
    input,
  );
}

/** List action-log entries → POST /api/agent/actions. */
export async function apiAgentActions(
  agent?: string,
): Promise<readonly AgentAction[]> {
  const data = await postJson<{ actions?: readonly AgentAction[] }>(
    "/api/agent/actions",
    agent !== undefined ? { agent } : {},
  );
  return data.actions ?? [];
}

/** Screen text for prompt injection → POST /api/agent/screen. */
export function apiAgentScreen(text: string): Promise<InjectionScreen> {
  return postJson<InjectionScreen>("/api/agent/screen", { text });
}

// ---------------------------------------------------------------------------
// Revenue & Rates domain types (M8). Mirror the JSON API response shapes.
//
// IMPORTANT: every *Minor money field arrives as a decimal-integer STRING (not
// a number) because rate amounts are stored/computed as bigint minor units and
// can exceed the JS safe-integer range. Keep them as strings on the wire;
// convert with `Number(x)` ONLY when handing a value to {@link formatMoney} for
// display. Timestamps arrive as ISO strings (or null). Success shapes are
// returned directly; errors arrive as an ErrorEnvelope (narrow with
// {@link asError}).
// ---------------------------------------------------------------------------

/** A yield rule's kind (M8): which context dimension it keys off, or a clamp. */
export type YieldRuleKind =
  | "occupancy"
  | "lead_time"
  | "day_of_week"
  | "length_of_stay"
  | "min_price"
  | "max_price";

/** A yield rule record (M8). Money fields are minor-unit strings (or null). */
export interface YieldRule {
  readonly id: string;
  /** The unit this rule scopes to, or null when it applies to all units. */
  readonly unitId: string | null;
  readonly name: string;
  readonly kind: string;
  readonly threshold: number;
  /** The rate adjustment in basis points (can be negative). */
  readonly adjustmentBps: number;
  readonly floorMinor: string | null;
  readonly ceilingMinor: string | null;
  readonly priority: number;
  readonly enabled: boolean;
  readonly createdAt: string | null;
}

/** The success shape returned by a live rate quote (M8). Money is a string. */
export interface RateSimulationResult {
  readonly baseRateMinor: string;
  readonly quotedRateMinor: string;
  readonly appliedRuleCount: number;
}

// ---------------------------------------------------------------------------
// Revenue & Rates service calls (M8). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// Success shapes are returned directly; errors arrive as an ErrorEnvelope
// (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/**
 * List yield rules → POST /api/revenue/rules/list. Omit `unitId` for every rule
 * on the tenant; pass one to restrict to that unit plus global rules.
 */
export async function apiListYieldRules(
  unitId?: string,
): Promise<readonly YieldRule[]> {
  const data = await postJson<{ rules: readonly YieldRule[] }>(
    "/api/revenue/rules/list",
    unitId !== undefined ? { unitId } : {},
  );
  return data.rules ?? [];
}

/** Create a yield rule → POST /api/revenue/rules/create. */
export function apiCreateYieldRule(input: {
  unitId?: string | null;
  name: string;
  kind: YieldRuleKind;
  threshold?: number;
  adjustmentBps?: number;
  floorMinor?: string | null;
  ceilingMinor?: string | null;
  priority?: number;
  enabled?: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/revenue/rules/create", input);
}

/** Enable or disable a yield rule → POST /api/revenue/rules/set-enabled. */
export function apiSetRuleEnabled(input: {
  ruleId: string;
  enabled: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/revenue/rules/set-enabled", input);
}

/** Delete a yield rule → POST /api/revenue/rules/delete. */
export function apiDeleteYieldRule(ruleId: string): Promise<CreateResult> {
  return postJson<CreateResult>("/api/revenue/rules/delete", { ruleId });
}

/** Compute a live dynamic rate quote → POST /api/revenue/quote. */
export function apiQuoteRate(input: {
  unitId?: string | null;
  baseRateMinor: string;
  occupancyPct: number;
  leadTimeDays: number;
  dayOfWeek: number;
  lengthOfStayNights: number;
}): Promise<RateSimulationResult | ErrorEnvelope> {
  return postJson<RateSimulationResult | ErrorEnvelope>("/api/revenue/quote", input);
}

// ---------------------------------------------------------------------------
// Revenue workspace: Overview, Rate Calendar, Rate Plans, Restrictions,
// Guardrails, Sync Health, Change History (M8). Money is minor-unit strings.
// ---------------------------------------------------------------------------

/** Overview KPI roll-up + per-property performance rows. */
export interface RevenueOverview {
  readonly kpis: {
    readonly grossRevenueMinor: string;
    readonly unitCount: number;
    readonly propertyCount: number;
    readonly avgOccupancy: number;
    readonly currency: string;
  };
  readonly properties: readonly {
    readonly propertyId: string;
    readonly propertyName: string;
    readonly city: string | null;
    readonly unitCount: number;
    readonly revenueMinor: string;
    readonly occupancy: number;
    readonly currency: string;
  }[];
}

/** Overview → POST /api/revenue/overview. */
export function apiRevenueOverview(propertyId?: string | null): Promise<RevenueOverview | ErrorEnvelope> {
  return postJson<RevenueOverview | ErrorEnvelope>("/api/revenue/overview", propertyId ? { propertyId } : {});
}

/** One cell of the rate calendar for a unit/day. */
export interface CalendarCell {
  readonly day: string;
  readonly rateMinor: string;
  readonly overridden: boolean;
  readonly stopSell: boolean;
  /** Per-rate-plan daily restriction indicators (present on rate-plan rows). */
  readonly minStay?: number;
  readonly maxStay?: number | null;
  readonly closedToArrival?: boolean;
  readonly closedToDeparture?: boolean;
  readonly hasRestriction?: boolean;
  /** Sellable rooms for this unit/date (unit-row cells only). */
  readonly availableRooms?: number;
}

/** One rate-plan sub-row under a room, with its own daily cells. */
export interface CalendarRatePlanRow {
  readonly ratePlanId: string;
  readonly ratePlanName: string;
  readonly baseRateMinor: string;
  readonly minStay: number;
  readonly maxStay: number | null;
  readonly minStayType: "arrival" | "through";
  readonly closedToArrival: boolean;
  readonly closedToDeparture: boolean;
  readonly cells: readonly CalendarCell[];
}

/** One row (unit) of the rate calendar grid. */
export interface CalendarRow {
  readonly unitId: string;
  readonly unitName: string;
  readonly propertyId: string;
  readonly currency: string;
  readonly ratePlanId: string | null;
  readonly baseRateMinor: string;
  readonly minStay: number;
  readonly maxStay: number | null;
  readonly closedToArrival: boolean;
  readonly closedToDeparture: boolean;
  readonly cells: readonly CalendarCell[];
  /** Physical sellable room count for the room type. */
  readonly countOfRooms?: number;
  /** Per-rate-plan sub-rows (Room → Rate Plan → daily cells). */
  readonly ratePlans?: readonly CalendarRatePlanRow[];
}

/** The full rate calendar for a date range. */
export interface RateCalendar {
  readonly from: string;
  readonly to: string;
  readonly dates: readonly string[];
  readonly rows: readonly CalendarRow[];
}

/** Rate calendar grid → POST /api/revenue/calendar. */
export function apiRateCalendar(input: {
  propertyId?: string | null;
  from: string;
  to: string;
}): Promise<RateCalendar | ErrorEnvelope> {
  return postJson<RateCalendar | ErrorEnvelope>("/api/revenue/calendar", input);
}

/** A small Channex sync summary echoed back after a write. */
export interface SyncSummary {
  readonly ratesPushed?: number;
  readonly restrictionsPushed?: number;
  readonly skipped?: boolean;
  /** True when the change was durably queued to the ARI/availability outbox. */
  readonly queued?: boolean;
  readonly availabilityQueued?: boolean;
  readonly ariQueued?: boolean;
}

/** Save one calendar cell (rate/stop-sell + optional restrictions) → auto-sync. */
export function apiSaveCalendarCell(input: {
  unitId: string;
  day: string;
  rateMinor?: string | null;
  isAvailable?: boolean;
  ratePlanId?: string | null;
  minStay?: number | null;
  minStayType?: "arrival" | "through";
  maxStay?: number | null;
  stopSell?: boolean;
  /**
   * Marks an EXPLICIT stop-sell toggle (vs the UI's default `isAvailable:true`).
   * The backend only folds stop_sell into the Channex DELTA when this is set,
   * so a rate-only edit never leaks `stop_sell:false` (Channex Test #2).
   */
  stopSellToggle?: boolean;
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
}): Promise<{ id: string; sync: SyncSummary } | ErrorEnvelope> {
  return postJson<{ id: string; sync: SyncSummary } | ErrorEnvelope>("/api/revenue/calendar/cell", input);
}

/** Bulk-update result / preview cell. */
export interface BulkCell {
  readonly day: string;
  readonly currentRateMinor: string;
  readonly newRateMinor: string;
}

/** Bulk update (or preview) a date range → POST /api/revenue/calendar/bulk. */
export function apiBulkUpdateCalendar(input: {
  unitId: string;
  from: string;
  to: string;
  daysOfWeek?: readonly number[];
  adjustPct?: number | null;
  setRateMinor?: string | null;
  stopSell?: boolean;
  minStay?: number | null;
  minStayType?: "arrival" | "through";
  maxStay?: number | null;
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
  ratePlanId?: string | null;
  preview?: boolean;
}): Promise<
  | { preview: true; count: number; cells: readonly BulkCell[] }
  | { updated: number; cells: readonly BulkCell[]; sync: SyncSummary }
  | ErrorEnvelope
> {
  return postJson("/api/revenue/calendar/bulk", input);
}

/** One line of a batch update (room + rate plan + date range + rate/restrictions). */
export interface BatchUpdateLine {
  readonly unitId: string;
  readonly ratePlanId: string;
  readonly from: string;
  readonly to: string;
  readonly setRateMinor?: string | null;
  readonly minStay?: number | null;
  readonly minStayType?: "arrival" | "through";
  readonly maxStay?: number | null;
  readonly stopSell?: boolean;
  readonly closedToArrival?: boolean;
  readonly closedToDeparture?: boolean;
}

/** A per-line summary echoed by the batch endpoint (preview + apply). */
export interface BatchLineSummary {
  readonly room: string;
  readonly ratePlan: string;
  readonly from: string;
  readonly to: string;
  readonly days: number;
  readonly rateMinor?: string;
  readonly minStay?: number;
  readonly minStayType?: "arrival" | "through";
  readonly maxStay?: number;
  readonly stopSell?: boolean;
  readonly closedToArrival?: boolean;
  readonly closedToDeparture?: boolean;
}

/** The common batch summary fields (preview + apply). */
export interface BatchSummary {
  readonly lines: readonly BatchLineSummary[];
  readonly lineCount: number;
  readonly roomsAffected: number;
  readonly ratePlansAffected: number;
  readonly datesAffected: number;
}

/**
 * Apply (or preview) a BATCH of update lines in ONE operation → ONE combined
 * ARI job → ONE Channex call. POST /api/revenue/calendar/batch.
 */
export function apiBatchUpdateCalendar(input: {
  updates: readonly BatchUpdateLine[];
  preview?: boolean;
}): Promise<
  | ({ preview: true } & BatchSummary)
  | ({ applied: true; ariJobId: string | null; sync: SyncSummary } & BatchSummary)
  | ErrorEnvelope
> {
  return postJson("/api/revenue/calendar/batch", input);
}

// --- Inventory management (inside Revenue & Rates) -------------------------

/** One inventory calendar cell: physical / booked / blocked / sellable + flags. */
export interface InventoryCell {
  readonly day: string;
  readonly physicalRooms: number;
  readonly booked: number;
  readonly blocked: number;
  readonly sellable: number;
  readonly closed: boolean;
  readonly hasOverride: boolean;
  readonly availableOverride: number | null;
}

/** One inventory calendar row (a room type/unit). */
export interface InventoryRow {
  readonly unitId: string;
  readonly unitName: string;
  readonly propertyId: string;
  readonly physicalRooms: number;
  readonly cells: readonly InventoryCell[];
}

export interface InventoryCalendar {
  readonly from: string;
  readonly to: string;
  readonly dates: readonly string[];
  readonly rows: readonly InventoryRow[];
}

export interface InventoryTabSummary {
  readonly physical: number;
  readonly booked: number;
  readonly blocked: number;
  readonly available: number;
  readonly roomTypes: number;
}

export interface InventoryBlock {
  readonly id: string;
  readonly propertyId: string;
  readonly unitId: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly quantity: number;
  readonly reason: string;
  readonly notes: string | null;
  readonly status: "active" | "released";
  readonly createdBy: string | null;
  readonly createdAt: string;
}

/** Inventory calendar grid → POST /api/revenue/inventory/calendar. */
export function apiInventoryCalendar(input: { propertyId?: string | null; from: string; to: string }): Promise<InventoryCalendar | ErrorEnvelope> {
  return postJson<InventoryCalendar | ErrorEnvelope>("/api/revenue/inventory/calendar", input);
}

/** Inventory property summary → POST /api/revenue/inventory/summary. */
export function apiInventorySummary(propertyId?: string | null): Promise<InventoryTabSummary | ErrorEnvelope> {
  return postJson<InventoryTabSummary | ErrorEnvelope>("/api/revenue/inventory/summary", propertyId ? { propertyId } : {});
}

/** Set the sellable rooms for one unit/date (optionally close) → auto-sync. */
export function apiInventorySetAvailable(input: { propertyId: string; unitId: string; day: string; availableRooms?: number | null; close?: boolean }): Promise<{ id: string; sync: SyncSummary } | ErrorEnvelope> {
  return postJson("/api/revenue/inventory/set-available", input);
}

/** Reopen a date (clear closure + override) → auto-sync. */
export function apiInventoryReopen(input: { propertyId: string; unitId: string; day: string }): Promise<{ id: string; sync: SyncSummary } | ErrorEnvelope> {
  return postJson("/api/revenue/inventory/reopen", input);
}

/** List inventory blocks → POST /api/revenue/inventory/blocks/list. */
export async function apiListInventoryBlocks(input: { propertyId: string; unitId?: string; activeOnly?: boolean }): Promise<readonly InventoryBlock[]> {
  const data = await postJson<{ blocks: readonly InventoryBlock[] }>("/api/revenue/inventory/blocks/list", input);
  return (data as { blocks?: readonly InventoryBlock[] }).blocks ?? [];
}

/** Create an inventory block → POST /api/revenue/inventory/blocks/create. */
export function apiCreateInventoryBlock(input: { propertyId: string; unitId: string; startDate: string; endDate: string; quantity: number; reason: string; notes?: string | null }): Promise<{ block: InventoryBlock; sync: SyncSummary } | ErrorEnvelope> {
  return postJson("/api/revenue/inventory/blocks/create", input);
}

/** Release an inventory block → POST /api/revenue/inventory/blocks/release. */
export function apiReleaseInventoryBlock(input: { propertyId: string; blockId: string }): Promise<{ block: InventoryBlock; sync: SyncSummary } | ErrorEnvelope> {
  return postJson("/api/revenue/inventory/blocks/release", input);
}

/** One line of a batch inventory update. */
export interface InventoryBatchLine {
  readonly unitId: string;
  readonly action: "set" | "block" | "release" | "close" | "reopen";
  readonly from: string;
  readonly to: string;
  readonly daysOfWeek?: readonly number[];
  readonly availableRooms?: number | null;
  readonly quantity?: number | null;
  readonly reason?: string;
  readonly notes?: string | null;
}

export interface InventoryBatchSummary {
  readonly lineCount: number;
  readonly roomsAffected: number;
  readonly datesAffected: number;
  readonly lines: readonly { readonly room: string; readonly action: string; readonly from: string; readonly to: string; readonly days: number; readonly availableRooms: number | null; readonly quantity: number | null; readonly reason: string }[];
}

/** Apply (or preview) a BATCH of inventory update lines → one operation. */
export function apiInventoryBatch(input: { propertyId: string; updates: readonly InventoryBatchLine[]; preview?: boolean }): Promise<
  | ({ preview: true; changes: readonly { room: string; day: string; oldAvailable: number; newAvailable: number; action: string }[] } & InventoryBatchSummary)
  | ({ applied: true; availabilityJobs: number; sync: SyncSummary } & InventoryBatchSummary)
  | ErrorEnvelope
> {
  return postJson("/api/revenue/inventory/batch", input);
}

/** A rate plan row for the Rate Plans + Restrictions tabs. */
export interface RatePlanRow {
  readonly id: string;
  readonly unitId: string;
  readonly unitName: string;
  readonly propertyId: string;
  readonly name: string;
  readonly rateMinor: string;
  readonly rateCurrency: string;
  readonly minStay: number;
  readonly minStayType?: "arrival" | "through";
  readonly maxStay: number | null;
  readonly stopSell?: boolean;
  readonly closedToArrival: boolean;
  readonly closedToDeparture: boolean;
}

/** List rate plans (across a property's units) → POST /api/revenue/rate-plans/list. */
export async function apiRevenueListRatePlans(propertyId?: string | null): Promise<readonly RatePlanRow[]> {
  const data = await postJson<{ plans: readonly RatePlanRow[] }>(
    "/api/revenue/rate-plans/list",
    propertyId ? { propertyId } : {},
  );
  return data.plans ?? [];
}

/** Create a rate plan → POST /api/revenue/rate-plans/create. */
export function apiRevenueCreateRatePlan(input: {
  unitId: string;
  name: string;
  rateMinor: string;
  rateCurrency?: string;
  minStay?: number;
  maxStay?: number;
}): Promise<{ id: string; sync: string | null } | ErrorEnvelope> {
  return postJson("/api/revenue/rate-plans/create", input);
}

/** Save rate-plan restrictions → POST /api/revenue/restrictions/save (auto-sync). */
export function apiSaveRestrictions(input: {
  ratePlanId: string;
  minStay?: number | null;
  minStayType?: "arrival" | "through";
  maxStay?: number | null;
  stopSell?: boolean;
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
}): Promise<{ id: string; sync: SyncSummary } | ErrorEnvelope> {
  return postJson("/api/revenue/restrictions/save", input);
}

/** ARI jobs technical table → /api/channex/ari-jobs. */
export interface AriJobRow {
  readonly id: string;
  readonly type?: string;
  readonly scope: string | null;
  readonly status: string;
  readonly attempts: number;
  readonly nextRetry: string | null;
  readonly taskIds: readonly string[];
  readonly createdAt: string;
  readonly errorClass?: string | null;
}
export function apiAriJobs(): Promise<{ rateJobs: readonly AriJobRow[]; availabilityJobs: readonly AriJobRow[] } | ErrorEnvelope> {
  return postJson("/api/channex/ari-jobs", {});
}

/** Full reconciliation (2-call, 500-day) → /api/channex/full-reconciliation. */
/** A run-specific Full Reconciliation history row (certification §8). */
export interface FullReconciliationRun {
  readonly id: string;
  readonly propertyId: string;
  readonly horizonDays: number;
  readonly rooms: number;
  readonly ratePlans: number;
  readonly availabilityValues: number;
  readonly ariValues: number;
  readonly availabilityTaskIds: readonly string[];
  readonly ratesTaskIds: readonly string[];
  readonly status: string;
  readonly errorSummary: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
}

export interface FullReconciliationResponse {
  readonly propertyId: string;
  readonly notProvisioned?: boolean;
  readonly mock?: boolean;
  readonly horizonDays: number;
  readonly rooms: number;
  readonly ratePlans: number;
  readonly availabilityTaskIds: readonly string[];
  readonly ratesTaskIds: readonly string[];
  readonly mappingIssues: readonly { readonly kind: string; readonly name: string }[];
  /** The run-specific history row id for THIS execution. */
  readonly runId?: string | null;
  readonly startedAt?: string;
  /** Recent run history (newest first), each with its OWN two task-id sets. */
  readonly runs?: readonly FullReconciliationRun[];
}
export function apiFullReconciliation(propertyId: string): Promise<FullReconciliationResponse | ErrorEnvelope> {
  return postJson<FullReconciliationResponse | ErrorEnvelope>("/api/channex/full-reconciliation", { propertyId });
}

/** Run-specific Full Reconciliation history → /api/channex/full-reconciliation/history. */
export async function apiFullReconciliationHistory(propertyId?: string | null, limit = 20): Promise<readonly FullReconciliationRun[]> {
  const data = await postJson<{ runs?: readonly FullReconciliationRun[] } | ErrorEnvelope>(
    "/api/channex/full-reconciliation/history",
    { ...(propertyId ? { propertyId } : {}), limit },
  );
  return (data as { runs?: readonly FullReconciliationRun[] }).runs ?? [];
}

/** A sanitized payload-preview value (date range + fields; no secrets). */
export interface PayloadPreviewValue {
  readonly rate_plan_id: string;
  readonly dateRange: string;
  readonly fields: Record<string, string | number | boolean>;
}
export interface PayloadPreview {
  readonly operation: string;
  readonly scope: string | null;
  readonly rooms: readonly string[];
  readonly ratePlans: readonly string[];
  readonly changedFields: readonly string[];
  readonly inputValueCount: number;
  readonly serializedValueCount: number;
  readonly compactionRatio: number | null;
  readonly values: readonly PayloadPreviewValue[];
  readonly taskIds: readonly string[];
}
/** Sanitized payload preview (admin/dev technical validation) — never secrets. */
export async function apiPayloadPreview(input: { jobId?: string; changes?: unknown[] }): Promise<PayloadPreview | ErrorEnvelope> {
  return postJson<PayloadPreview | ErrorEnvelope>("/api/channex/payload-preview", input);
}

/** Retry failed ARI + availability jobs, then drain → /api/integrations/channex/process-availability. */
export function apiRetrySync(): Promise<unknown> {
  return postJson("/api/integrations/channex/process-availability", {});
}

/** Property pricing guardrails (min/max price bounds). */
export interface Guardrails {
  readonly propertyId: string;
  readonly minPriceMinor: string | null;
  readonly maxPriceMinor: string | null;
  readonly currency: string;
}

/** Get guardrails → POST /api/revenue/guardrails/get. */
export function apiGetGuardrails(propertyId: string): Promise<Guardrails | ErrorEnvelope> {
  return postJson<Guardrails | ErrorEnvelope>("/api/revenue/guardrails/get", { propertyId });
}

/** Save guardrails → POST /api/revenue/guardrails/save. */
export function apiSaveGuardrails(input: {
  propertyId: string;
  minPriceMinor?: string | null;
  maxPriceMinor?: string | null;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/revenue/guardrails/save", input);
}

/** A per-property sync health row. */
export interface SyncHealthRow {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly health: "healthy" | "warning" | "error" | "idle";
  readonly lastActivityAt: string | null;
  readonly lastOperation: string | null;
  readonly recent: readonly {
    readonly operation: string;
    readonly status: string;
    readonly at: string;
    readonly errorSummary: string | null;
  }[];
}

/** Sync health → POST /api/revenue/sync-health. */
export async function apiSyncHealth(propertyId?: string | null): Promise<readonly SyncHealthRow[]> {
  const data = await postJson<{ properties: readonly SyncHealthRow[] }>(
    "/api/revenue/sync-health",
    propertyId ? { propertyId } : {},
  );
  return data.properties ?? [];
}

/** A change-history item (rate push / sync activity). */
export interface HistoryItem {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly operation: string;
  readonly status: string;
  readonly at: string;
  readonly detail: unknown;
  readonly errorSummary: string | null;
}

/** Change history → POST /api/revenue/history. */
export async function apiRevenueHistory(input: { propertyId?: string | null; limit?: number }): Promise<readonly HistoryItem[]> {
  const data = await postJson<{ items: readonly HistoryItem[] }>("/api/revenue/history", input);
  return data.items ?? [];
}

/** Simulate a dynamic rate → POST /api/revenue/simulate (alias of quote). */
export function apiSimulateRate(input: {
  unitId?: string | null;
  baseRateMinor: string;
  occupancyPct: number;
  leadTimeDays: number;
  dayOfWeek: number;
  lengthOfStayNights: number;
}): Promise<RateSimulationResult | ErrorEnvelope> {
  return postJson<RateSimulationResult | ErrorEnvelope>("/api/revenue/simulate", input);
}

// ---------------------------------------------------------------------------
// Automation domain types (M8). Mirror the JSON API response shapes.
//
// Automation is trigger-based workflow rules across the guest lifecycle: a rule
// maps a lifecycle TRIGGER to an ACTION (optionally after a delay), and firing a
// trigger appends a run to an immutable log. Timestamps arrive as ISO strings
// (or null). Success shapes are returned directly; errors arrive as an
// ErrorEnvelope (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** A guest-lifecycle trigger a rule keys off (M8). */
export type AutomationTrigger =
  | "booking_confirmed"
  | "check_in"
  | "check_out"
  | "booking_cancelled"
  | "payment_received"
  | "review_received";

/** The action a rule runs when its trigger fires (M8). */
export type AutomationAction =
  | "send_message"
  | "create_task"
  | "notify_operator"
  | "apply_tag";

/** The resolved status of an automation run (M8). */
export type AutomationRunStatus = "executed" | "skipped" | "failed";

/** An automation rule record (M8). */
export interface AutomationRule {
  readonly id: string;
  readonly name: string;
  readonly trigger: string;
  readonly action: string;
  /** The optional action payload (message template / tag / task title). */
  readonly actionParam: string | null;
  readonly delayMinutes: number;
  readonly enabled: boolean;
  readonly createdAt: string | null;
}

/** A single automation-run log entry (M8). */
export interface AutomationRun {
  readonly id: string;
  readonly ruleId: string;
  readonly trigger: string;
  readonly action: string;
  readonly status: string;
  readonly detail: string;
  readonly createdAt: string | null;
}

/** The success shape returned by firing a trigger (M8). */
export interface FireResult {
  readonly planned: number;
  readonly executed: number;
  readonly skipped: number;
  readonly runs: readonly AutomationRun[];
}

// ---------------------------------------------------------------------------
// Automation service calls (M8). Each mirrors the existing style: POST JSON,
// return the typed shape, unwrap the list envelope where present. Success
// shapes are returned directly; errors arrive as an ErrorEnvelope (narrow with
// {@link asError}).
// ---------------------------------------------------------------------------

/** List automation rules → POST /api/automation/rules/list. */
export async function apiListAutomationRules(): Promise<
  readonly AutomationRule[]
> {
  const data = await postJson<{ rules: readonly AutomationRule[] }>(
    "/api/automation/rules/list",
    {},
  );
  return data.rules ?? [];
}

/** Create an automation rule → POST /api/automation/rules/create. */
export function apiCreateAutomationRule(input: {
  name: string;
  trigger: AutomationTrigger;
  action: AutomationAction;
  actionParam?: string | null;
  delayMinutes?: number;
  enabled?: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/automation/rules/create", input);
}

/** Enable or disable a rule → POST /api/automation/rules/set-enabled. */
export function apiSetAutomationRuleEnabled(input: {
  ruleId: string;
  enabled: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/automation/rules/set-enabled", input);
}

/** Delete an automation rule → POST /api/automation/rules/delete. */
export function apiDeleteAutomationRule(
  ruleId: string,
): Promise<CreateResult> {
  return postJson<CreateResult>("/api/automation/rules/delete", { ruleId });
}

/** Fire a trigger → POST /api/automation/fire. */
export function apiFireTrigger(
  trigger: AutomationTrigger,
): Promise<FireResult | ErrorEnvelope> {
  return postJson<FireResult | ErrorEnvelope>("/api/automation/fire", {
    trigger,
  });
}

/**
 * List recent automation runs → POST /api/automation/runs/list. Omit `ruleId`
 * for every recent run; pass one to restrict to that rule's runs.
 */
export async function apiListAutomationRuns(
  ruleId?: string,
): Promise<readonly AutomationRun[]> {
  const data = await postJson<{ runs: readonly AutomationRun[] }>(
    "/api/automation/runs/list",
    ruleId !== undefined ? { ruleId } : {},
  );
  return data.runs ?? [];
}

// ---------------------------------------------------------------------------
// Operations domain types (M8). Mirror the JSON API response shapes.
//
// Operations is the tenant's task board that keeps units guest-ready:
// housekeeping, maintenance, inspections, turnovers, and restocks. Dates arrive
// as YYYY-MM-DD strings (or null); timestamps as ISO strings. Success shapes
// are returned directly; errors arrive as an ErrorEnvelope (narrow with
// {@link asError}).
// ---------------------------------------------------------------------------

/** The class of operational work. */
export type OpsTaskKind =
  | "housekeeping"
  | "maintenance"
  | "inspection"
  | "turnover"
  | "restock";

/** The urgency of an operational task (urgent is most urgent). */
export type OpsTaskPriority = "low" | "normal" | "high" | "urgent";

/** The lifecycle status of an operational task. */
export type OpsTaskStatus =
  | "open"
  | "assigned"
  | "in_progress"
  | "blocked"
  | "done"
  | "cancelled";

/** The owning department of an operational task. */
export type OpsDepartment =
  | "housekeeping"
  | "maintenance"
  | "kitchen"
  | "guest_services"
  | "front_office"
  | "security"
  | "sales"
  | "marketing"
  | "finance"
  | "management"
  | "other";

/** How an operational task originated. */
export type OpsTaskSource =
  | "manual"
  | "booking"
  | "turnover"
  | "guest_request"
  | "maintenance_issue"
  | "automation";

/** A single checklist item's completion state. */
export interface OpsChecklistItemState {
  readonly label: string;
  readonly done: boolean;
}

/** An operational task record. */
export interface OpsTask {
  readonly id: string;
  readonly unitId: string | null;
  readonly unitName: string | null;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly kind: OpsTaskKind;
  readonly title: string;
  readonly notes: string | null;
  readonly priority: OpsTaskPriority;
  readonly status: OpsTaskStatus;
  readonly assignee: string | null;
  readonly department: OpsDepartment;
  readonly source: OpsTaskSource;
  readonly reservationId: string | null;
  readonly createdBy: string | null;
  readonly blockedReason: string | null;
  readonly checklistTemplateId: string | null;
  readonly checklistState: readonly OpsChecklistItemState[];
  /** Due date `YYYY-MM-DD`, or null. */
  readonly dueOn: string | null;
  readonly completedAt: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

/** Per-status task counts plus the overdue tally (the operations summary). */
export interface OpsSummary {
  readonly open: number;
  readonly assigned: number;
  readonly inProgress: number;
  readonly blocked: number;
  readonly done: number;
  readonly cancelled: number;
  readonly overdue: number;
}

/** The turnover stage machine. */
export type OpsTurnoverStage =
  | "checkout_due"
  | "dirty"
  | "cleaning"
  | "inspection"
  | "ready"
  | "blocked";

/** A turnover record. */
export interface OpsTurnover {
  readonly id: string;
  readonly unitId: string;
  readonly unitName: string | null;
  readonly propertyName: string | null;
  readonly checkoutReservationId: string | null;
  readonly nextReservationId: string | null;
  readonly checkoutOn: string | null;
  readonly nextArrivalOn: string | null;
  readonly readyBy: string | null;
  readonly stage: OpsTurnoverStage;
  readonly housekeepingAssignee: string | null;
  readonly taskId: string | null;
  readonly checklistTemplateId: string | null;
  readonly checklistState: readonly OpsChecklistItemState[];
  readonly blockedReason: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

/** An operational issue record. */
export interface OpsIssue {
  readonly id: string;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly unitId: string | null;
  readonly unitName: string | null;
  readonly title: string;
  readonly description: string | null;
  readonly category: string;
  readonly priority: OpsTaskPriority;
  readonly status: string;
  readonly reportedBy: string | null;
  readonly reportedAt: string | null;
  readonly assignee: string | null;
  readonly taskId: string | null;
  readonly guestImpact: boolean;
  readonly resolvedAt: string | null;
}

/** A reusable checklist template. */
export interface OpsChecklistTemplate {
  readonly id: string;
  readonly name: string;
  readonly department: OpsDepartment;
  readonly propertyId: string | null;
  readonly items: readonly string[];
  readonly archived: boolean;
}

/** A derived arrival/checkout movement in the overview. */
export interface OpsMovement {
  readonly reservationId: string;
  readonly kind: "arrival" | "checkout";
  readonly date: string;
  readonly unitId: string;
  readonly unitName: string | null;
  readonly propertyName: string | null;
  readonly guestName: string | null;
}

/** A unit's operational readiness. */
export interface OpsUnitReadiness {
  readonly unitId: string;
  readonly unitName: string | null;
  readonly propertyName: string | null;
  readonly status: OpsTurnoverStage | "unknown";
  readonly nextArrivalOn: string | null;
  readonly atRisk: boolean;
  readonly turnoverId: string | null;
  readonly blockedReason: string | null;
}

/** The operations command-centre overview. */
export interface OpsOverview {
  readonly todayIso: string;
  readonly arrivalsToday: readonly OpsMovement[];
  readonly checkoutsToday: readonly OpsMovement[];
  readonly unitsReady: number;
  readonly unitsTracked: number;
  readonly readiness: readonly OpsUnitReadiness[];
}

/** A team member's derived operational workload. */
export interface OpsTeamMember {
  readonly assignee: string;
  readonly assignedToday: number;
  readonly inProgress: number;
  readonly completed: number;
  readonly overdue: number;
  readonly openTotal: number;
  readonly currentTask: string | null;
  readonly nextTask: string | null;
}

/** Per-department operational workload. */
export interface OpsDepartmentSnapshot {
  readonly department: OpsDepartment;
  readonly assigned: number;
  readonly inProgress: number;
  readonly completed: number;
  readonly overdue: number;
  readonly blocked: number;
  readonly openTotal: number;
}

/** Operational performance metrics over a window. */
export interface OpsPerformance {
  readonly fromIso: string;
  readonly toIso: string;
  readonly tasksCompleted: number;
  readonly tasksCreated: number;
  readonly completionRate: number | null;
  readonly onTimeRate: number | null;
  readonly avgCompletionHours: number | null;
  readonly avgIssueResolutionHours: number | null;
  readonly onTimeTurnoverRate: number | null;
  readonly overdueNow: number;
}

/** A per-property row for the Overview portfolio comparison table. */
export interface OpsPropertyComparisonRow {
  readonly propertyId: string;
  readonly propertyName: string | null;
  readonly readyUnits: number;
  readonly trackedUnits: number;
  readonly openTasks: number;
  readonly overdueTasks: number;
  readonly blockedTasks: number;
  readonly onTimeCompletionRate: number | null;
}

// ---------------------------------------------------------------------------
// Operations service calls (M8). Each mirrors the existing style: POST JSON,
// return the typed shape, unwrap the list envelope where present.
// ---------------------------------------------------------------------------

/** List operational tasks → POST /api/operations/tasks/list. */
export async function apiListOpsTasks(input?: {
  status?: OpsTaskStatus;
  unitId?: string;
  propertyId?: string;
  department?: OpsDepartment;
  priority?: OpsTaskPriority;
  source?: OpsTaskSource;
  assignee?: string;
}): Promise<readonly OpsTask[]> {
  const data = await postJson<{ tasks: readonly OpsTask[] }>(
    "/api/operations/tasks/list",
    input ?? {},
  );
  return data.tasks ?? [];
}

/** Create an operational task → POST /api/operations/tasks/create. */
export function apiCreateOpsTask(input: {
  kind: OpsTaskKind;
  title: string;
  notes?: string | null;
  unitId?: string | null;
  propertyId?: string | null;
  priority?: OpsTaskPriority;
  assignee?: string | null;
  dueOn?: string | null;
  department?: OpsDepartment;
  source?: OpsTaskSource;
  reservationId?: string | null;
  createdBy?: string | null;
  checklistTemplateId?: string | null;
  checklistState?: readonly OpsChecklistItemState[];
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/tasks/create", input);
}

/** Change a task's status → POST /api/operations/tasks/status. */
export function apiUpdateOpsTaskStatus(input: {
  taskId: string;
  status: OpsTaskStatus;
  blockedReason?: string | null;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/tasks/status", input);
}

/** Assign (or clear) a task's assignee → POST /api/operations/tasks/assign. */
export function apiAssignOpsTask(input: {
  taskId: string;
  assignee: string | null;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/tasks/assign", input);
}

/** Delete a task → POST /api/operations/tasks/delete. */
export function apiDeleteOpsTask(taskId: string): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/tasks/delete", { taskId });
}

/** Operations summary → POST /api/operations/summary. Optional property scope. */
export function apiOpsSummary(propertyId?: string): Promise<OpsSummary | ErrorEnvelope> {
  return postJson<OpsSummary | ErrorEnvelope>(
    "/api/operations/summary",
    propertyId !== undefined && propertyId !== "" ? { propertyId } : {},
  );
}

/** Operations overview (arrivals/checkouts/readiness) → POST /api/operations/overview. */
export function apiOpsOverview(input?: {
  todayIso?: string;
  propertyId?: string;
}): Promise<OpsOverview | ErrorEnvelope> {
  return postJson<OpsOverview | ErrorEnvelope>("/api/operations/overview", input ?? {});
}

/** Team activity → POST /api/operations/team. Optional property scope. */
export async function apiOpsTeam(propertyId?: string): Promise<readonly OpsTeamMember[]> {
  const data = await postJson<{ team: readonly OpsTeamMember[] }>(
    "/api/operations/team",
    propertyId !== undefined && propertyId !== "" ? { propertyId } : {},
  );
  return data.team ?? [];
}

/** Department snapshot → POST /api/operations/departments. Optional property scope. */
export async function apiOpsDepartments(propertyId?: string): Promise<readonly OpsDepartmentSnapshot[]> {
  const data = await postJson<{ departments: readonly OpsDepartmentSnapshot[] }>(
    "/api/operations/departments",
    propertyId !== undefined && propertyId !== "" ? { propertyId } : {},
  );
  return data.departments ?? [];
}

/** Operational performance over a window → POST /api/operations/performance. */
export function apiOpsPerformance(input: {
  fromIso: string;
  toIso: string;
  propertyId?: string;
}): Promise<OpsPerformance | ErrorEnvelope> {
  return postJson<OpsPerformance | ErrorEnvelope>("/api/operations/performance", input);
}

/** Portfolio per-property scorecard → POST /api/operations/property-comparison. */
export async function apiOpsPropertyComparison(): Promise<readonly OpsPropertyComparisonRow[]> {
  const data = await postJson<{ rows: readonly OpsPropertyComparisonRow[] }>(
    "/api/operations/property-comparison",
    {},
  );
  return data.rows ?? [];
}

/** List turnovers → POST /api/operations/turnovers/list. Optional property scope. */
export async function apiListTurnovers(input?: {
  activeOnly?: boolean;
  propertyId?: string;
}): Promise<readonly OpsTurnover[]> {
  const data = await postJson<{ turnovers: readonly OpsTurnover[] }>(
    "/api/operations/turnovers/list",
    input ?? {},
  );
  return data.turnovers ?? [];
}

/** Create a turnover → POST /api/operations/turnovers/create. */
export function apiCreateTurnover(input: {
  unitId: string;
  checkoutReservationId?: string | null;
  nextReservationId?: string | null;
  checkoutOn?: string | null;
  nextArrivalOn?: string | null;
  readyBy?: string | null;
  housekeepingAssignee?: string | null;
  checklistTemplateId?: string | null;
  checklistState?: readonly OpsChecklistItemState[];
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/turnovers/create", input);
}

/** Advance a turnover's stage → POST /api/operations/turnovers/stage. */
export function apiUpdateTurnoverStage(input: {
  turnoverId: string;
  stage: OpsTurnoverStage;
  blockedReason?: string | null;
  housekeepingAssignee?: string | null;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/turnovers/stage", input);
}

/** List issues → POST /api/operations/issues/list. Optional status + property scope. */
export async function apiListIssues(input?: {
  status?: string;
  propertyId?: string;
}): Promise<readonly OpsIssue[]> {
  const data = await postJson<{ issues: readonly OpsIssue[] }>(
    "/api/operations/issues/list",
    input ?? {},
  );
  return data.issues ?? [];
}

/** Create an issue → POST /api/operations/issues/create. */
export function apiCreateIssue(input: {
  title: string;
  description?: string | null;
  propertyId?: string | null;
  unitId?: string | null;
  category?: string;
  priority?: OpsTaskPriority;
  reportedBy?: string | null;
  assignee?: string | null;
  guestImpact?: boolean;
  createTask?: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/issues/create", input);
}

/** Update an issue's status → POST /api/operations/issues/status. */
export function apiUpdateIssueStatus(input: {
  issueId: string;
  status: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/issues/status", input);
}

/** List checklist templates → POST /api/operations/checklists/list. */
export async function apiListChecklists(): Promise<readonly OpsChecklistTemplate[]> {
  const data = await postJson<{ templates: readonly OpsChecklistTemplate[] }>(
    "/api/operations/checklists/list",
    {},
  );
  return data.templates ?? [];
}

/** Create a checklist template → POST /api/operations/checklists/create. */
export function apiCreateChecklist(input: {
  name: string;
  department?: OpsDepartment;
  propertyId?: string | null;
  items: readonly string[];
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/checklists/create", input);
}

/** Delete a checklist template → POST /api/operations/checklists/delete. */
export function apiDeleteChecklist(templateId: string): Promise<CreateResult> {
  return postJson<CreateResult>("/api/operations/checklists/delete", { templateId });
}

// ---------------------------------------------------------------------------
// Marketing & SEO domain types (M8). Mirror the JSON API response shapes.
//
// Marketing & SEO is the tenant's tooling to grow direct demand: campaigns
// across channels with budget/spend + performance counters, and SEO landing
// pages that can be audited and published.
//
// IMPORTANT: every *Minor money field arrives as a decimal-integer STRING (not
// a number) because budget/spend amounts are stored as bigint. Keep them as
// strings on the wire; convert with `Number(x)` ONLY when handing a value to
// {@link formatMoney} for display. Ratios (ctr, conversionRate) are
// dimensionless numbers. Timestamps arrive as ISO strings; dates as
// YYYY-MM-DD. Success shapes are returned directly; errors arrive as an
// ErrorEnvelope (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** The marketing channel a campaign runs on. */
export type CampaignChannel = "email" | "social" | "search" | "display";

/** The lifecycle status of a campaign. */
export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "active"
  | "paused"
  | "completed";

/** A campaign record with computed performance metrics. Money fields are minor strings. */
export interface Campaign {
  readonly id: string;
  readonly name: string;
  readonly channel: CampaignChannel;
  readonly status: CampaignStatus;
  readonly budgetMinor: string;
  readonly spendMinor: string;
  readonly impressions: number;
  readonly clicks: number;
  readonly conversions: number;
  readonly currency: string;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
  readonly createdAt: string;
  /** Click-through ratio in `[0, 1]`. */
  readonly ctr: number;
  /** Conversion ratio in `[0, 1]`. */
  readonly conversionRate: number;
  /** Cost per click in minor units (string). */
  readonly cpcMinor: string;
  /** Remaining budget in minor units (string). */
  readonly budgetRemainingMinor: string;
}

/** A landing page record (SEO-auditable content). */
export interface LandingPage {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly metaDescription: string | null;
  readonly heading: string | null;
  readonly body: string | null;
  readonly published: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** The result of an SEO audit: a 0..100 score and a list of human issues. */
export interface SeoAudit {
  readonly score: number;
  readonly issues: readonly string[];
}

// ---------------------------------------------------------------------------
// Marketing & SEO service calls (M8). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// ---------------------------------------------------------------------------

/** List campaigns → POST /api/marketing/campaigns/list. */
export async function apiListCampaigns(input?: {
  status?: CampaignStatus;
}): Promise<readonly Campaign[]> {
  const data = await postJson<{ campaigns: readonly Campaign[] }>(
    "/api/marketing/campaigns/list",
    input ?? {},
  );
  return data.campaigns ?? [];
}

/** Create a campaign → POST /api/marketing/campaigns/create. */
export function apiCreateCampaign(input: {
  name: string;
  channel: CampaignChannel;
  status?: CampaignStatus;
  budgetMinor?: string;
  currency?: string;
  startsOn?: string | null;
  endsOn?: string | null;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/marketing/campaigns/create", input);
}

/** Set a campaign's status → POST /api/marketing/campaigns/status. */
export function apiSetCampaignStatus(input: {
  campaignId: string;
  status: CampaignStatus;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/marketing/campaigns/status", input);
}

/** Record campaign stats (increment counters/spend) → POST /api/marketing/campaigns/stats. */
export function apiRecordCampaignStats(input: {
  campaignId: string;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  spendMinor?: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/marketing/campaigns/stats", input);
}

/** Delete a campaign → POST /api/marketing/campaigns/delete. */
export function apiDeleteCampaign(campaignId: string): Promise<CreateResult> {
  return postJson<CreateResult>("/api/marketing/campaigns/delete", {
    campaignId,
  });
}

/** List landing pages → POST /api/marketing/pages/list. */
export async function apiListLandingPages(): Promise<readonly LandingPage[]> {
  const data = await postJson<{ pages: readonly LandingPage[] }>(
    "/api/marketing/pages/list",
    {},
  );
  return data.pages ?? [];
}

/** Create a landing page → POST /api/marketing/pages/create. */
export function apiCreateLandingPage(input: {
  title: string;
  slug?: string;
  metaDescription?: string;
  heading?: string;
  body?: string;
}): Promise<{ id: string; slug: string } | ErrorEnvelope> {
  return postJson<{ id: string; slug: string } | ErrorEnvelope>(
    "/api/marketing/pages/create",
    input,
  );
}

/** Publish/unpublish a landing page → POST /api/marketing/pages/publish. */
export function apiSetPagePublished(input: {
  pageId: string;
  published: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/marketing/pages/publish", input);
}

/** Audit a landing page's SEO → POST /api/marketing/pages/audit. */
export function apiAuditPage(
  pageId: string,
): Promise<SeoAudit | ErrorEnvelope> {
  return postJson<SeoAudit | ErrorEnvelope>("/api/marketing/pages/audit", {
    pageId,
  });
}

/** Delete a landing page → POST /api/marketing/pages/delete. */
export function apiDeleteLandingPage(pageId: string): Promise<CreateResult> {
  return postJson<CreateResult>("/api/marketing/pages/delete", { pageId });
}

// ---------------------------------------------------------------------------
// Admin & Settings domain types (M8). Mirror the JSON API response shapes.
//
// Timestamps arrive as ISO strings (or null). Success shapes are returned
// directly; errors arrive as an ErrorEnvelope (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** An RBAC role a team member can hold (matches the core-api rbac ROLES). */
export type TeamRole = "Owner" | "Manager" | "Staff" | "Accountant";

/** A team member record. */
export interface TeamMember {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly role: TeamRole;
  readonly active: boolean;
  readonly createdAt: string | null;
}

/** A tenant configuration setting (one value per known key). */
export interface TenantSetting {
  readonly key: string;
  readonly value: string;
  readonly updatedAt: string | null;
}

/**
 * A serializable snapshot of the RBAC permission matrix for the Admin UI:
 * the roles (columns), the operations (rows), and a nested
 * `role -> operation -> boolean` map.
 */
export interface RoleMatrix {
  readonly roles: readonly string[];
  readonly operations: readonly string[];
  readonly matrix: Readonly<Record<string, Readonly<Record<string, boolean>>>>;
}

// ---------------------------------------------------------------------------
// Admin & Settings service calls (M8). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// ---------------------------------------------------------------------------

/** List team members → POST /api/admin/team/list. */
export async function apiListTeamMembers(): Promise<readonly TeamMember[]> {
  const data = await postJson<{ members: readonly TeamMember[] }>(
    "/api/admin/team/list",
    {},
  );
  return data.members ?? [];
}

/** Add a team member → POST /api/admin/team/add. */
export function apiAddTeamMember(input: {
  email: string;
  displayName?: string;
  role: TeamRole;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/admin/team/add", input);
}

/** Set a member's role → POST /api/admin/team/set-role. */
export function apiSetMemberRole(input: {
  memberId: string;
  role: TeamRole;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/admin/team/set-role", input);
}

/** Activate/deactivate a member → POST /api/admin/team/set-active. */
export function apiSetMemberActive(input: {
  memberId: string;
  active: boolean;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/admin/team/set-active", input);
}

/** Remove a team member → POST /api/admin/team/remove. */
export function apiRemoveTeamMember(memberId: string): Promise<CreateResult> {
  return postJson<CreateResult>("/api/admin/team/remove", { memberId });
}

/** Get all tenant settings → POST /api/admin/settings/get. */
export async function apiGetSettings(): Promise<readonly TenantSetting[]> {
  const data = await postJson<{ settings: readonly TenantSetting[] }>(
    "/api/admin/settings/get",
    {},
  );
  return data.settings ?? [];
}

/** Set a tenant setting → POST /api/admin/settings/set. */
export function apiSetSetting(input: {
  key: string;
  value: string;
}): Promise<{ key: string } | ErrorEnvelope> {
  return postJson<{ key: string } | ErrorEnvelope>(
    "/api/admin/settings/set",
    input,
  );
}

/** Get the RBAC role/permission matrix → POST /api/admin/roles/matrix. */
export function apiGetRoleMatrix(): Promise<RoleMatrix> {
  return postJson<RoleMatrix>("/api/admin/roles/matrix", {});
}

// ---------------------------------------------------------------------------
// Channex OTA integration domain types (M3 Channex). Mirror the JSON API
// response shapes.
//
// Web boundary: this file speaks ONLY to the API over fetch — the master
// Channex API key lives on the backend and is NEVER present in any of these
// payloads. Iframe URLs come from the backend `connect` response; the client
// NEVER constructs them and NEVER embeds a key.
//
// Money for per-channel financials is NOT provided by Channex — it is computed
// client-side from the AVOS bookings register ({@link apiListBookings}). Those
// *Minor fields remain decimal-integer STRINGS; convert with `Number(x)` ONLY
// when handing a value to {@link formatMoney} for display.
// ---------------------------------------------------------------------------

/** An OTA offered in the connect catalogue. */
export interface OtaCatalogueItem {
  /** The OTA code, e.g. `"airbnb"`, `"booking_com"`. */
  readonly code: string;
  /** Display name, e.g. `"Booking.com"`. */
  readonly name: string;
  /** Short marketing description. */
  readonly description: string;
  /** The Channex channel code used to scope the connect iframe (e.g. "BDC"). */
  readonly channexCode?: string;
  /** Per-OTA capability flags (drives onboarding behaviour; never fabricated). */
  readonly capabilities?: ChannelCapabilityFlags;
}

/** A property row for the Channel Manager property selector (§2/§38). */
export interface ChannexPropertyOption {
  readonly id: string;
  readonly name: string;
  /** True when provisioned to a REAL (non-mock) Channex property. */
  readonly provisioned: boolean;
  /** How many OTA channels are currently `active` for this property. */
  readonly connectedChannels: number;
}

/** Real KPI figures for the Channel Manager landing (§5), scoped or aggregate. */
export interface ChannexOverview {
  /** null = portfolio-wide (All properties); else the scoped property id. */
  readonly scope: string | null;
  readonly connectedChannels: number;
  readonly activeChannels: number;
  readonly needsAttention: number;
  readonly otaBookingsThisMonth: number;
  readonly otaGrossThisMonthMinor: string;
  readonly otaCommissionThisMonthMinor: string;
}

/** The result of reconciling connections against the live provider (§39). */
export interface ChannexReconcileEntry {
  readonly propertyId: string;
  readonly channexPropertyId: string | null;
  readonly notProvisioned: boolean;
  readonly mock: boolean;
  readonly providerReadOk: boolean;
  readonly connections: readonly ChannelConnectionRow[];
}

/** Real per-channel detail for the Channel Detail page (§18). */
/**
 * The user-facing onboarding stage of a channel connection (derived server-side
 * from the reconciled provider state — never fabricated). Drives the Airbnb
 * onboarding UI (§ activation): Connection required → Mapping required → Ready
 * to activate → Active, with Needs attention for provider errors.
 */
export type ChannexConnectionStage =
  | "connection_required"
  | "mapping_required"
  | "ready_to_activate"
  | "active"
  | "needs_attention";

export interface ChannexChannelDetail {
  readonly propertyId: string;
  readonly otaCode: string;
  readonly name: string;
  readonly channexCode: string | null;
  readonly capabilities: ChannelCapabilityFlags | null;
  /** The derived onboarding stage from the real reconciled state. */
  readonly stage: ChannexConnectionStage;
  readonly connection: (ChannelConnectionRow & { readonly lastSyncAt: string | null }) | null;
  readonly bookingsThisMonth: number;
  readonly grossThisMonthMinor: string;
  readonly commissionThisMonthMinor: string;
  /** How many of this month's bookings carried a provider-supplied commission. */
  readonly commissionRows: number;
}

/** The live connection status of an OTA channel (from the backend). */
export type ChannexConnectionStatus =
  | "not_connected"
  | "setup_required"
  | "connecting"
  | "active"
  | "paused"
  | "attention"
  | "sync_error"
  | "disconnected";

/**
 * A per-OTA channel connection row (from `listChannexConnections`). Some fields
 * may be absent from the API response depending on provisioning state; consume
 * defensively (optional chaining / defaults).
 */
export interface ChannelConnectionRow {
  readonly id: string;
  readonly propertyId: string;
  readonly otaCode: string;
  readonly status: ChannexConnectionStatus;
  readonly channexChannelId: string | null;
  readonly mappedRooms: number;
  readonly totalRooms: number;
  readonly mappedRatePlans: number;
  readonly totalRatePlans: number;
  /** ISO timestamp of the last successful sync, or null if never synced. */
  readonly lastSyncAt: string | null;
  readonly lastWarning: string | null;
  readonly lastError: string | null;
  /**
   * False when Channex mapping state could not be reliably determined; display
   * a status label, never a count. Optional on the wire — treat `undefined` as
   * "known" (true) at call sites.
   */
  readonly mappingKnown?: boolean;
  /**
   * When this connection FIRST completed its required setup lifecycle. `null`
   * (or undefined on older responses) means PROVISIONAL / never completed — NOT
   * a Connected Channel: it is excluded from the Connected list + count and is
   * invisible to the sync/reconciliation workers. Once set it is never cleared.
   */
  readonly setupCompletedAt?: string | null;
  /**
   * The existing-reservation BOOTSTRAP lifecycle for the connection (overbooking
   * safety). Optional on the wire — treat `undefined` as `"active"` (the
   * backward-compat default) at call sites so older responses render as live.
   */
  readonly bootstrapState?: ChannexBootstrapState;
  readonly bootstrapImportRequestedAt?: string | null;
  readonly bootstrapCompletedAt?: string | null;
  readonly bootstrapImportedCount?: number;
  readonly bootstrapSkippedCount?: number;
  readonly bootstrapUnmappedCount?: number;
  readonly bootstrapEarliestArrival?: string | null;
  readonly bootstrapLatestDeparture?: string | null;
  readonly bootstrapError?: string | null;
  /**
   * The in-flight Safe Deactivate + Remove lifecycle, or null/undefined when
   * NOT in a removal flow. A non-null value drives the transient card state
   * ("Deactivating…"/"Removing…"/"Removal failed" + Retry) and EXCLUDES the
   * connection from the healthy Connected/Active count. Once removal reaches
   * `removed` the row is physically gone (so it never arrives with this value).
   */
  readonly removalState?: ChannexRemovalState | null;
  readonly removalRequestedAt?: string | null;
  /** The sanitized provider error when removalState is a *_failed state. */
  readonly removalError?: string | null;
}

/** The in-flight Safe-Remove lifecycle (mirrors the backend `removal_state`). */
export type ChannexRemovalState =
  | "deactivating"
  | "deactivated"
  | "removing"
  | "removed"
  | "deactivate_failed"
  | "remove_failed";

/** The existing-reservation bootstrap lifecycle state (mirrors the backend). */
export type ChannexBootstrapState =
  // Pre-bootstrap: a NEW future-pull-capable connection, still mapping. Blocked.
  | "connection"
  | "pending"
  | "importing"
  | "inventory_recalculating"
  | "ready_to_sync"
  | "active"
  | "needs_attention"
  | "error";

/** The operator's explicit choice on the Existing Bookings step (§6/§7). */
export type ExistingBookingsDeclared = "none" | "import";

/** Durable bootstrap status for one connection (from /bootstrap/status). */
export interface ChannexBootstrapStatus {
  readonly propertyId: string;
  readonly otaCode: string;
  readonly state: ChannexBootstrapState;
  readonly importRequestedAt: string | null;
  readonly completedAt: string | null;
  readonly importedCount: number;
  readonly skippedCount: number;
  readonly unmappedCount: number;
  readonly earliestArrival: string | null;
  readonly latestDeparture: string | null;
  readonly error: string | null;
  /**
   * TRUE when the connection is parked in `needs_attention` specifically because
   * BOTH import sources returned zero bookings and the operator did not declare
   * "no existing bookings" — the UI shows the actionable Retry Import state.
   */
  readonly zeroButExpected?: boolean;
}

/** The result of requesting an existing-booking import (/bootstrap/import). */
export interface ChannexBootstrapImportResult {
  readonly supported: boolean;
  readonly state: ChannexBootstrapState;
  readonly seen: number;
  readonly imported: number;
  readonly skipped: number;
  readonly unmapped: number;
  readonly earliestArrival: string | null;
  readonly latestDeparture: string | null;
  readonly error: string | null;
  /** TRUE when zero bookings were found but the operator expected some (§6/§7). */
  readonly zeroButExpected?: boolean;
}

/** A single readiness checklist item. */
export interface ReadinessCheck {
  readonly field: string;
  readonly label: string;
  readonly ok: boolean;
}

/** The readiness gate result for a property (or an error envelope). */
export interface ReadinessResult {
  readonly propertyId: string;
  readonly ready: boolean;
  readonly items: readonly ReadinessCheck[];
  readonly missing: readonly ReadinessCheck[];
}

/** The catalogue + current connection state for a property. */
export interface CatalogueResult {
  readonly propertyId: string | null;
  readonly catalogue: readonly OtaCatalogueItem[];
  readonly connections: readonly ChannelConnectionRow[];
}

/** The success shape returned by provisioning a property onto Channex. */
export interface ProvisionResult {
  readonly channexPropertyId: string;
  readonly roomTypes: number;
  readonly ratePlans: number;
}

/** The Channex iframe descriptor — a one-time token + URL (never a key). */
export interface ChannexIframe {
  readonly token: string;
  readonly url: string;
}

/** The success shape returned by connecting an OTA. */
export interface ConnectResult {
  readonly propertyId: string;
  readonly otaCode: string;
  /**
   * The iframe scoping mode chosen from the REAL reconciled state:
   *   - `"create"`: no channel exists yet — the embedded wizard offers only
   *     this OTA for creation (available_channels), NOT a result filter.
   *   - `"manage"`: a channel already exists — the wizard is filtered to it
   *     (channels_filter) for configuration/mapping.
   */
  readonly mode?: "create" | "manage";
  /** True when the provider already reports a channel for this OTA. */
  readonly channelExists?: boolean;
  /** The derived onboarding stage from the real reconciled state. */
  readonly stage?: ChannexConnectionStage;
  /** The reconciled connection row (real state), or null when none yet. */
  readonly connection?: (ChannelConnectionRow & { readonly lastSyncAt: string | null }) | null;
  /** The Channex iframe to load, or null (e.g. mock provider without a key). */
  readonly iframe: ChannexIframe | null;
}

/** A single Channex activity/timeline row. */
export interface ChannexActivityRow {
  readonly id: string;
  readonly otaCode: string | null;
  readonly operation: string;
  readonly status: string;
  /** ISO timestamp when the operation started, or null. */
  readonly startedAt: string | null;
  /** ISO timestamp when the operation completed, or null. */
  readonly completedAt: string | null;
  readonly errorSummary: string | null;
  readonly detail: unknown;
}

// ---------------------------------------------------------------------------
// Channex service calls (M3 Channex). Each mirrors the existing style: POST
// JSON, return the typed shape, unwrap the list envelope where present.
// Success shapes are returned directly; errors arrive as an ErrorEnvelope
// (narrow with {@link asError}).
// ---------------------------------------------------------------------------

/** Catalogue + connections → POST /api/channex/catalogue. */
export async function apiChannexCatalogue(
  propertyId?: string,
): Promise<CatalogueResult> {
  const data = await postJson<CatalogueResult>(
    "/api/channex/catalogue",
    propertyId !== undefined ? { propertyId } : {},
  );
  return {
    propertyId: data.propertyId ?? null,
    catalogue: data.catalogue ?? [],
    connections: data.connections ?? [],
  };
}

/** List tenant properties for the Channel Manager selector → POST /api/channex/properties. */
export async function apiChannexProperties(): Promise<readonly ChannexPropertyOption[]> {
  const data = await postJson<{ properties: readonly ChannexPropertyOption[] }>(
    "/api/channex/properties",
    {},
  );
  return data.properties ?? [];
}

/** Real KPI overview (scoped or portfolio) → POST /api/channex/overview. */
export function apiChannexOverview(
  propertyId?: string,
): Promise<ChannexOverview | ErrorEnvelope> {
  return postJson<ChannexOverview | ErrorEnvelope>(
    "/api/channex/overview",
    propertyId !== undefined ? { propertyId } : {},
  );
}

/** Reconcile connections from the live provider → POST /api/channex/reconcile. */
export async function apiChannexReconcile(
  propertyId?: string,
): Promise<readonly ChannexReconcileEntry[]> {
  const data = await postJson<{ reconciled: readonly ChannexReconcileEntry[] }>(
    "/api/channex/reconcile",
    propertyId !== undefined ? { propertyId } : {},
  );
  return data.reconciled ?? [];
}

/** Real per-channel detail → POST /api/channex/channel-detail. */
export function apiChannexChannelDetail(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<ChannexChannelDetail | ErrorEnvelope> {
  return postJson<ChannexChannelDetail | ErrorEnvelope>(
    "/api/channex/channel-detail",
    input,
  );
}

// ---------------------------------------------------------------------------
// Booking sync + diagnostics (§7/§8/§22/§24) — local/admin developer surface.
// ---------------------------------------------------------------------------

/** Summary of a booking-feed sync run. */
export interface BookingSyncSummary {
  readonly fetched: number;
  readonly created: number;
  readonly modified: number;
  readonly cancelled: number;
  readonly duplicates: number;
  readonly mappingRequired: number;
  readonly skipped: number;
  readonly failed: number;
  readonly acknowledged: number;
}

/** Result of the manual Sync bookings now action. */
export interface BookingSyncResult {
  readonly bookings: BookingSyncSummary;
  readonly availability: {
    readonly processed: number;
    readonly succeeded: number;
    readonly failed: number;
    readonly skipped: number;
  } | null;
}

/** Local integration diagnostics (§24). Never carries the API key. */
export interface ChannexDiagnostics {
  readonly environment: string;
  readonly apiConnectivity: string;
  readonly mappedProperties: number;
  readonly mappedRoomTypes: number;
  readonly mappedRatePlans: number;
  readonly unacknowledgedRevisions: number;
  readonly pendingAvailabilityJobs: number;
  readonly lastBookingSyncAt: string | null;
  readonly lastAvailabilitySyncAt: string | null;
  // Rates/restrictions outbox + full-reconciliation + task-id fields (additive).
  readonly failedAvailabilityJobs?: number;
  readonly availabilitySyncHealth?: string;
  readonly pendingRateJobs?: number;
  readonly failedRateJobs?: number;
  readonly ratesSyncHealth?: string;
  readonly lastRatesSyncAt?: string | null;
  readonly lastFullReconciliationAt?: string | null;
  readonly lastAvailabilityTaskIds?: readonly string[];
  readonly lastRatesTaskIds?: readonly string[];
}

/** A durable inbound-revision log entry (§22). */
export interface ChannexBookingEvent {
  readonly id: string;
  readonly propertyId: string | null;
  readonly channexBookingId: string | null;
  readonly channexRevisionId: string;
  readonly externalUniqueId: string | null;
  readonly otaReservationCode: string | null;
  readonly otaName: string | null;
  readonly revisionStatus: string | null;
  readonly outcome: string;
  readonly reservationId: string | null;
  readonly errorSummary: string | null;
  readonly acknowledged: boolean;
  readonly receivedAt: string | null;
  readonly processedAt: string | null;
}

/** Trigger the Booking Revision Feed sync → POST /api/integrations/channex/sync-bookings. */
export function apiChannexSyncBookings(
  maxRevisions?: number,
): Promise<BookingSyncResult | ErrorEnvelope> {
  return postJson<BookingSyncResult | ErrorEnvelope>(
    "/api/integrations/channex/sync-bookings",
    maxRevisions !== undefined ? { maxRevisions } : {},
  );
}

/** Local integration diagnostics → POST /api/channex/diagnostics. */
export function apiChannexDiagnostics(): Promise<ChannexDiagnostics> {
  return postJson<ChannexDiagnostics>("/api/channex/diagnostics", {});
}

/** A per-property full ARI sync result. */
export interface FullSyncResultRow {
  readonly propertyId: string;
  readonly horizonDays: number;
  readonly rooms: number;
  readonly ratePlans: number;
  readonly availabilityPushed: number;
  readonly ratesPushed: number;
  readonly restrictionsPushed: number;
  readonly batchesFailed?: number;
}

/**
 * Initial / full ARI sync (AVOS → Channex) → POST
 * /api/integrations/channex/sync-inventory. Pushes AUTHORITATIVE availability +
 * real rate/restrictions for the horizon (>=365 days). Idempotent.
 */
export function apiChannexSyncInventory(input?: {
  propertyId?: string;
  horizonDays?: number;
}): Promise<{ results: readonly FullSyncResultRow[] } | ErrorEnvelope> {
  return postJson("/api/integrations/channex/sync-inventory", input ?? {});
}

/** Test staging connectivity → POST /api/channex/test-connection. */
export function apiChannexTestConnection(): Promise<{
  connected: boolean;
  message: string;
}> {
  return postJson<{ connected: boolean; message: string }>(
    "/api/channex/test-connection",
    {},
  );
}

/** Drain the availability push queue → POST /api/integrations/channex/process-availability. */
export function apiChannexProcessAvailability(): Promise<{
  processed: number;
  succeeded: number;
  failed: number;
  skipped: number;
} | ErrorEnvelope> {
  return postJson("/api/integrations/channex/process-availability", {});
}

/** List durable booking events (actionable conflicts) → POST /api/channex/booking-events. */
export async function apiChannexBookingEvents(input?: {
  propertyId?: string;
  actionableOnly?: boolean;
}): Promise<readonly ChannexBookingEvent[]> {
  const data = await postJson<{ events: readonly ChannexBookingEvent[] }>(
    "/api/channex/booking-events",
    input ?? {},
  );
  return data.events ?? [];
}

/** Reprocess a stored booking revision after fixing mapping → POST /api/channex/reprocess-booking-event. */
export function apiChannexReprocessBookingEvent(
  channexRevisionId: string,
): Promise<{ reservationId: string } | ErrorEnvelope> {
  return postJson("/api/channex/reprocess-booking-event", { channexRevisionId });
}

/** Property readiness gate → POST /api/channex/readiness. */
export function apiChannexReadiness(
  propertyId?: string,
): Promise<ReadinessResult | ErrorEnvelope> {
  return postJson<ReadinessResult | ErrorEnvelope>(
    "/api/channex/readiness",
    propertyId !== undefined ? { propertyId } : {},
  );
}

/** Provision the property onto Channex → POST /api/channex/provision. */
export function apiChannexProvision(
  propertyId?: string,
): Promise<ProvisionResult | ErrorEnvelope> {
  return postJson<ProvisionResult | ErrorEnvelope>(
    "/api/channex/provision",
    propertyId !== undefined ? { propertyId } : {},
  );
}

/** Connect an OTA (mints a fresh iframe token) → POST /api/channex/connect. */
export function apiChannexConnect(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<ConnectResult | ErrorEnvelope> {
  return postJson<ConnectResult | ErrorEnvelope>(
    "/api/channex/connect",
    input,
  );
}

/**
 * Cancel/abandon a PROVISIONAL (never-completed) channel setup →
 * POST /api/channex/connect/cancel. Cleans up the external draft channel and
 * deletes the provisional row. Refuses to cancel a completed connection.
 */
export function apiChannexCancelConnect(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>("/api/channex/connect/cancel", input);
}

/** The terminal outcome of a Safe Deactivate + Remove for a COMPLETED channel. */
export interface ChannelRemoveResult {
  readonly propertyId: string;
  readonly otaCode: string;
  /** The terminal removal state reached. */
  readonly finalState: "removed" | "deactivate_failed" | "remove_failed";
  readonly deactivated: boolean;
  readonly removed: boolean;
  /** The sanitized provider error message when a phase failed. */
  readonly error?: string;
}

/**
 * Safe Deactivate + Remove a COMPLETED channel → POST /api/channex/connect/remove.
 * The backend FIRST soft-deactivates the channel at Channex, THEN removes it,
 * and only after Channex confirms removal deletes the LOCAL connection row.
 * Existing reservations / guests / history are NEVER deleted. Idempotent — call
 * again to Retry a `deactivate_failed`/`remove_failed` outcome.
 */
export function apiChannexRemoveChannel(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<ChannelRemoveResult | ErrorEnvelope> {
  return postJson<ChannelRemoveResult | ErrorEnvelope>(
    "/api/channex/connect/remove",
    input,
  );
}

/** List channel connections → POST /api/channex/connections/list. */
export async function apiChannexConnections(
  propertyId?: string,
): Promise<readonly ChannelConnectionRow[]> {
  const data = await postJson<{ connections: readonly ChannelConnectionRow[] }>(
    "/api/channex/connections/list",
    propertyId !== undefined ? { propertyId } : {},
  );
  return data.connections ?? [];
}

/** Set a connection status → POST /api/channex/connection/set-status. */
export function apiChannexSetConnectionStatus(input: {
  otaCode: string;
  status: ChannexConnectionStatus;
  propertyId?: string;
}): Promise<CreateResult> {
  return postJson<CreateResult>(
    "/api/channex/connection/set-status",
    input,
  );
}

/** List Channex activity → POST /api/channex/activity/list. */
export async function apiChannexActivity(input?: {
  otaCode?: string;
  propertyId?: string;
}): Promise<readonly ChannexActivityRow[]> {
  const data = await postJson<{ activity: readonly ChannexActivityRow[] }>(
    "/api/channex/activity/list",
    input ?? {},
  );
  return data.activity ?? [];
}

/** Registered webhook event types → POST /api/channex/webhook-events. */
export async function apiChannexWebhookEvents(): Promise<readonly string[]> {
  const data = await postJson<{ eventTypes: readonly string[] }>(
    "/api/channex/webhook-events",
    {},
  );
  return data.eventTypes ?? [];
}

// ---------------------------------------------------------------------------
// Channel onboarding: prep · migration · inventory verification · go-live
// ---------------------------------------------------------------------------

/** Per-channel capability flags (§11). */
export interface ChannelCapabilityFlags {
  readonly supportsExistingBookingImport: boolean;
  readonly supportsFutureBookingPull: boolean;
  readonly supportsCommission: boolean;
  readonly supportsGuestDetailsOnImport: boolean;
}

/** Channel-specific preparation copy (§5), with {property} resolved. */
export interface ChannelPrep {
  readonly otaCode: string;
  readonly name: string;
  readonly channexCode: string;
  readonly capabilities: ChannelCapabilityFlags;
  readonly prep: {
    readonly title: string;
    readonly intro: string;
    readonly note: string | null;
    readonly cta: string;
  };
}

/** Fetch channel-specific prep copy → POST /api/channex/prep. */
export function apiChannexPrep(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<ChannelPrep | ErrorEnvelope> {
  return postJson<ChannelPrep | ErrorEnvelope>("/api/channex/prep", input);
}

/** The outcome of an initial reservation migration (§10/§11). */
export interface MigrateResult {
  readonly supported: boolean;
  readonly seen: number;
  readonly imported: number;
  readonly skipped: number;
  readonly fallbackReason?: string;
}

/** Pull existing/future reservations for a channel → POST /api/channex/migrate. */
export function apiChannexMigrate(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<MigrateResult | ErrorEnvelope> {
  return postJson<MigrateResult | ErrorEnvelope>("/api/channex/migrate", input);
}

/**
 * Import existing/future reservations for a channel SAFELY (bootstrap) →
 * POST /api/channex/bootstrap/import. AVOS orchestrates the import (the customer
 * does not log into Channex); authoritative availability stays gated until this
 * completes.
 */
export function apiChannexBootstrapImport(input: {
  otaCode: string;
  propertyId?: string;
  /** The operator's explicit zero-booking choice (§6/§7). */
  existingBookingsDeclared?: ExistingBookingsDeclared;
}): Promise<ChannexBootstrapImportResult | ErrorEnvelope> {
  return postJson<ChannexBootstrapImportResult | ErrorEnvelope>(
    "/api/channex/bootstrap/import",
    input,
  );
}

/** Durable bootstrap status for a connection → POST /api/channex/bootstrap/status. */
export function apiChannexBootstrapStatus(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<ChannexBootstrapStatus | ErrorEnvelope> {
  return postJson<ChannexBootstrapStatus | ErrorEnvelope>(
    "/api/channex/bootstrap/status",
    input,
  );
}

/** A single overbook day surfaced by inventory verification (§14). */
export interface OverbookDayRow {
  readonly unitId: string;
  readonly date: string;
  readonly occupancy: number;
  readonly capacity: number;
}

/** The inventory verification result (§13/§14). */
export interface InventoryVerification {
  readonly report: {
    readonly reservationCount: number;
    readonly unitCount: number;
    readonly overbooks: readonly OverbookDayRow[];
    readonly ok: boolean;
  };
  readonly intervals: readonly {
    readonly unitId: string;
    readonly checkIn: string;
    readonly checkOut: string;
    readonly source: string;
  }[];
  /** OTA codes that cannot auto-verify existing-reservation import (§14). */
  readonly unverifiable: readonly string[];
}

/** Verify inventory before go-live → POST /api/channex/verify-inventory. */
export function apiChannexVerifyInventory(
  propertyId?: string,
): Promise<InventoryVerification | ErrorEnvelope> {
  return postJson<InventoryVerification | ErrorEnvelope>(
    "/api/channex/verify-inventory",
    propertyId !== undefined ? { propertyId } : {},
  );
}

/** A connection row in the go-live checklist. */
export interface GoLiveConnection {
  readonly otaCode: string;
  readonly status: ChannexConnectionStatus;
  readonly mappedRooms: number | null;
  readonly totalRooms: number | null;
  readonly mappedRatePlans: number | null;
  readonly totalRatePlans: number | null;
}

/** The go-live checklist aggregate (§15). */
export interface GoLiveChecklist {
  readonly propertyId: string;
  readonly provisioned: boolean;
  readonly units: { readonly total: number; readonly mapped: number };
  readonly ratePlans: { readonly total: number; readonly mapped: number };
  readonly connections: readonly GoLiveConnection[];
}

/** Fetch the go-live checklist → POST /api/channex/go-live-checklist. */
export function apiChannexGoLiveChecklist(
  propertyId?: string,
): Promise<GoLiveChecklist | ErrorEnvelope> {
  return postJson<GoLiveChecklist | ErrorEnvelope>(
    "/api/channex/go-live-checklist",
    propertyId !== undefined ? { propertyId } : {},
  );
}

/** The result of activating a channel (§16). */
export interface ActivateResult {
  readonly otaCode: string;
  readonly availabilityPushed: number;
  readonly ratesPushed: number;
  readonly restrictionsPushed: number;
  readonly activatedAt: string;
}

/** Activate a channel (go-live; ARI push) → POST /api/channex/activate. */
export function apiChannexActivate(input: {
  otaCode: string;
  propertyId?: string;
}): Promise<ActivateResult | ErrorEnvelope> {
  return postJson<ActivateResult | ErrorEnvelope>(
    "/api/channex/activate",
    input,
  );
}

// ---------------------------------------------------------------------------
// Accounts module — financial overlay on existing AVOS records (AVOS Books logic)
// ---------------------------------------------------------------------------

/** A revenue stream in the Accounts module. */
export type AccountsStream = "rooms" | "dining" | "photo" | "scooty" | "other";

/** A stay row in Stays & Invoices (reservation + financial overlay). */
export interface AccountsStayRow {
  readonly reservationId: string;
  readonly invoiceId: string | null;
  readonly propertyId: string;
  readonly propertyName: string | null;
  readonly unitId: string | null;
  readonly unitName: string | null;
  readonly guestName: string | null;
  readonly guestEmail: string | null;
  readonly guestPhone: string | null;
  readonly channel: string | null;
  readonly checkIn: string | null;
  readonly checkOut: string | null;
  readonly status: string;
  readonly currency: string;
  readonly hasInvoice: boolean;
  readonly invoiceNo: string | null;
  readonly lifecycle: "issued" | "archived" | "void" | null;
  readonly issuedOn: string | null;
  readonly baseMinor: string;
  readonly discountMinor: string;
  readonly netMinor: string;
  readonly gstMinor: string;
  readonly grossMinor: string;
  readonly commissionMinor: string;
  readonly paidMinor: string;
  readonly paymentStatus: "settled" | "clearing" | "part" | "due";
  readonly collection: "guest" | "ota";
  readonly byStream: Readonly<Record<string, number>>;
  readonly salespersonId: string | null;
  readonly salespersonName: string | null;
  readonly salespersonCommissionMinor: string;
  readonly advanceMinor: string;
  readonly receivedOn: string | null;
  readonly lines: readonly {
    readonly stream: string;
    readonly rateMinor: string;
    readonly qty: string;
    readonly description: string | null;
    readonly gstOverride: string | null;
  }[];
}

/** A recorded payment on an invoice (detail view). */
export interface AccountsInvoicePayment {
  readonly id: string;
  readonly amountMinor: string;
  readonly method: string;
  readonly provider: string | null;
  readonly receivedOn: string | null;
  readonly reference: string | null;
}

/** Full invoice detail: the stay overlay row + its payment history. */
export interface AccountsInvoiceDetail {
  readonly stay: AccountsStayRow;
  readonly payments: readonly AccountsInvoicePayment[];
}

/** An expense row (with derived ITC). */
export interface AccountsExpenseRow {
  readonly id: string;
  readonly propertyId: string | null;
  readonly date: string;
  readonly category: string;
  readonly note: string | null;
  readonly amountMinor: string;
  readonly gstRate: number;
  readonly itcMinor: string;
  readonly method: string;
  readonly currency: string;
  readonly isDirect: boolean;
  readonly claimItc: boolean;
  readonly paidAccount: string | null;
}

/** A cash movement row. */
export interface AccountsCashRow {
  readonly id: string;
  readonly propertyId: string | null;
  readonly date: string;
  readonly kind: string;
  readonly amountMinor: string;
  readonly note: string | null;
  readonly currency: string;
}

/** A voucher row. */
export interface AccountsVoucherRow {
  readonly id: string;
  readonly code: string;
  readonly active: boolean;
  readonly type: "pct" | "fixed";
  readonly value: number;
  readonly maxCapMinor: number | null;
  readonly minSpendMinor: number | null;
  readonly limit: number | null;
  readonly directOnly: boolean;
  readonly validFrom: string | null;
  readonly validTo: string | null;
  /** "promo" = booking discount code; "gift" = stored monetary value. */
  readonly kind: "promo" | "gift";
  readonly name: string | null;
  readonly eligibility: "all" | "new" | "returning";
  readonly propertyScope: "all" | "selected";
  readonly propertyIds: readonly string[];
  /** Gift vouchers only: original stored value (minor units). */
  readonly initialValueMinor: number | null;
  /** Gift vouchers only: Σ redemptions (minor units). */
  readonly redeemedMinor: number;
  /** Gift vouchers only: remaining balance (minor units). */
  readonly remainingMinor: number | null;
  readonly notes: string | null;
  readonly archived: boolean;
  readonly redemptions: number;
}

/** One gift-voucher redemption ledger row. */
export interface AccountsVoucherRedemptionRow {
  readonly id: string;
  readonly date: string | null;
  readonly amountMinor: string;
  readonly invoiceId: string | null;
  readonly reservationId: string | null;
  readonly note: string | null;
  readonly createdBy: string | null;
}

/** Staff employment types. */
export type AccountsEmploymentType = "full_time" | "part_time" | "contract" | "casual";
/** Staff commission models. */
export type AccountsCommissionType = "none" | "pct" | "fixed";
/** Staff / payroll payment methods. */
export type AccountsStaffPaymentMethod = "bank" | "cash" | "upi";

/** A staff / payroll person row (Staff & Payroll). */
export interface AccountsPayrollRow {
  readonly id: string;
  readonly propertyId: string | null;
  readonly name: string;
  readonly baseSalaryMinor: string;
  readonly commissionPct: number;
  readonly currency: string;
  readonly active: boolean;
  readonly role: string;
  readonly employmentType: AccountsEmploymentType;
  readonly mobile: string | null;
  readonly email: string | null;
  readonly joiningDate: string | null;
  readonly commissionType: AccountsCommissionType;
  readonly commissionValueMinor: string;
  readonly paymentMethod: AccountsStaffPaymentMethod;
  readonly accountHolder: string | null;
  readonly bankName: string | null;
  readonly accountLast4: string | null;
  readonly ifsc: string | null;
  readonly upiId: string | null;
  readonly pan: string | null;
  readonly notes: string | null;
}

/** Aggregate staff summary for the Team tab cards. */
export interface AccountsStaffSummary {
  readonly currency: string;
  readonly totalStaff: number;
  readonly activeStaff: number;
  readonly monthlySalaryCostMinor: string;
  readonly pendingPayrollMinor: string;
}

/** A single computed / persisted payroll line. */
export interface AccountsPayrollLine {
  readonly id: string | null;
  readonly personId: string;
  readonly name: string;
  readonly role: string | null;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly baseSalaryMinor: string;
  readonly additionalMinor: string;
  readonly deductionsMinor: string;
  readonly netMinor: string;
  readonly currency: string;
  readonly paymentStatus: "pending" | "paid";
  readonly paidOn: string | null;
  readonly paymentMethod: AccountsStaffPaymentMethod | null;
  readonly paymentReference: string | null;
}

/** A payroll run header row. */
export interface AccountsPayrollRun {
  readonly id: string;
  readonly periodMonth: string;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly status: "draft" | "ready" | "paid";
  readonly currency: string;
  readonly totalMinor: string;
  readonly baseMinor: string;
  readonly additionalMinor: string;
  readonly pendingMinor: string;
  readonly lineCount: number;
  readonly paidCount: number;
}

/** A payslip row (payroll line joined to run + property). */
export interface AccountsPayslipRow {
  readonly lineId: string;
  readonly personId: string;
  readonly name: string;
  readonly role: string | null;
  readonly periodMonth: string;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly baseSalaryMinor: string;
  readonly additionalMinor: string;
  readonly deductionsMinor: string;
  readonly grossMinor: string;
  readonly netMinor: string;
  readonly currency: string;
  readonly paymentStatus: "pending" | "paid";
  readonly paidOn: string | null;
  readonly paymentMethod: AccountsStaffPaymentMethod | null;
  readonly paymentReference: string | null;
}

/** Full payslip detail (line + person snapshot). */
export interface AccountsPayslipDetail extends AccountsPayslipRow {
  readonly mobile: string | null;
  readonly email: string | null;
  readonly employmentType: AccountsEmploymentType | null;
  readonly paidAccount: string | null;
}

/** A sales-team performance row. */
export interface AccountsSalesTeamRow {
  readonly id: string;
  readonly name: string;
  readonly propertyId: string | null;
  readonly commissionPct: number;
  readonly conversions: number;
  readonly convertedNetMinor: string;
  readonly commissionMinor: string;
  readonly baseSalaryMinor: string;
  readonly payrollCostMinor: string;
  readonly currency: string;
}

/** A per-property cash-in-hand row. */
export interface AccountsCashInHandRow {
  readonly propertyId: string;
  readonly propertyName: string | null;
  readonly currency: string;
  readonly cashFromStaysMinor: string;
  readonly pettyCashSpendMinor: string;
  readonly bankMovementsMinor: string;
  readonly expectedMinor: string;
  readonly countedMinor: string | null;
  readonly varianceMinor: string | null;
}

/** The Cash & Banks overview summary. */
export interface AccountsCashBanksOverview {
  readonly currency: string;
  readonly cashInHandMinor: string;
  readonly bankBalanceMinor: string;
  readonly pendingCashMinor: string;
  readonly varianceMinor: string;
}

/** A bank account (book balance derived; full number never exposed). */
export interface AccountsBankAccountRow {
  readonly id: string;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly name: string;
  readonly bankName: string;
  readonly accountType: string;
  readonly last4: string | null;
  readonly ifsc: string | null;
  readonly branch: string | null;
  readonly upiId: string | null;
  readonly purpose: string | null;
  readonly isDefault: boolean;
  readonly status: "active" | "archived";
  readonly currency: string;
  readonly note: string | null;
  readonly bookBalanceMinor: string;
  readonly moneyInMinor: string;
  readonly moneyOutMinor: string;
  readonly lastMovementOn: string | null;
}

/** A bank-account transaction with a running balance. */
export interface AccountsBankTxnRow {
  readonly id: string;
  readonly date: string;
  readonly kind: string;
  readonly amountMinor: string;
  readonly mode: string | null;
  readonly reference: string | null;
  readonly note: string | null;
  readonly createdBy: string | null;
  readonly reversedOf: string | null;
  readonly transferGroupId: string | null;
  readonly runningMinor: string;
}

/** Bank account detail (account + running-balance transactions). */
export interface AccountsBankAccountDetail {
  readonly account: AccountsBankAccountRow;
  readonly txns: readonly AccountsBankTxnRow[];
}

/** A consolidated internal-movement row (cash + bank). */
export interface AccountsMovementRow {
  readonly id: string;
  readonly source: "cash" | "bank";
  readonly date: string;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly movement: string;
  readonly kind: string;
  readonly amountMinor: string;
  readonly reference: string | null;
  readonly note: string | null;
  readonly createdBy: string | null;
  readonly bankAccountId: string | null;
  readonly bankAccountName: string | null;
  readonly transferGroupId: string | null;
  readonly isReversal: boolean;
}

/** A cashbook line with a running physical-cash balance. */
export interface AccountsCashbookRow {
  readonly date: string;
  readonly reference: string;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly description: string;
  readonly type: string;
  readonly cashInMinor: string;
  readonly cashOutMinor: string;
  readonly runningMinor: string;
  readonly source: string;
  readonly currency: string;
}

/** A recent-activity feed item. */
export interface AccountsRecentActivity {
  readonly date: string;
  readonly kind: "stay" | "expense" | "cash";
  readonly description: string;
  readonly amountMinor: string;
  readonly currency: string;
}

/** The Accounts overview summary. */
export interface AccountsOverviewData {
  readonly currency: string;
  readonly revenueExGstMinor: string;
  readonly gstCollectedMinor: string;
  readonly commissionsMinor: string;
  readonly expensesMinor: string;
  readonly itcMinor: string;
  readonly netGstPayableMinor: string;
  readonly operatingResultMinor: string;
  readonly receivablesMinor: string;
  readonly byChannel: readonly { readonly channel: string; readonly netMinor: string }[];
  readonly byStream: readonly { readonly stream: string; readonly netMinor: string }[];
  readonly cashInHandMinor: string;
  readonly cashBalanceMinor: string;
  readonly recentActivity: readonly AccountsRecentActivity[];
}

/** A property's accounting settings (specified premises + management fee). */
export interface AccountsPropertySettings {
  readonly propertyId: string;
  readonly propertyName: string | null;
  readonly gstSpecified: boolean;
  readonly managementFeePct: number;
  readonly currency: string;
}

/** An itemised owner statement row. */
export interface AccountsOwnerStatementRow {
  readonly propertyId: string;
  readonly propertyName: string | null;
  readonly currency: string;
  readonly managementFeePct: number;
  readonly streams: readonly { readonly stream: string; readonly netMinor: string }[];
  readonly grossExGstMinor: string;
  readonly costs: readonly {
    readonly key: string;
    readonly label: string;
    readonly amountMinor: string;
  }[];
  readonly totalCostsMinor: string;
  readonly retainedResultMinor: string;
  readonly managementFeeMinor: string;
  readonly ownerShareMinor: string;
  /** Output GST collected on this property's stays. */
  readonly gstCollectedMinor: string;
  /** Eligible input tax credit from this property's expenses. */
  readonly itcMinor: string;
  /** Net GST payable = output GST − ITC. */
  readonly netGstPayableMinor: string;
  /** Per revenue-stream contributing stays (drill-down). */
  readonly streamDetails: readonly {
    readonly stream: string;
    readonly records: readonly {
      readonly label: string;
      readonly sublabel: string | null;
      readonly amountMinor: string;
    }[];
  }[];
  /** Per cost-key contributing records (drill-down). */
  readonly costDetails: readonly {
    readonly key: string;
    readonly records: readonly {
      readonly label: string;
      readonly sublabel: string | null;
      readonly amountMinor: string;
    }[];
  }[];
  readonly grossRevenueMinor: string;
  readonly commissionMinor: string;
  readonly expensesMinor: string;
  readonly ownerDueMinor: string;
}

/** A chart-of-accounts group with its accounts. */
export interface AccountsChartGroup {
  readonly group: "income" | "direct_costs" | "operating_expenses" | "assets" | "liabilities";
  readonly label: string;
  readonly accounts: readonly {
    readonly code: string;
    readonly label: string;
    readonly amountMinor: string;
    readonly currency: string;
  }[];
  readonly subtotalMinor: string;
}

/** A ledger line. */
export interface AccountsLedgerLine {
  readonly date: string;
  readonly kind: "revenue" | "commission" | "gst" | "expense" | "cash";
  readonly description: string;
  readonly amountMinor: string;
  readonly currency: string;
}

const acct = <T,>(path: string, body: unknown): Promise<T> => postJson<T>(path, body);
const acctProp = (propertyId?: string): Record<string, string> =>
  propertyId !== undefined ? { propertyId } : {};

/** Properties for the Accounts property selector. */
export async function apiAccountsProperties(): Promise<readonly Property[]> {
  const d = await acct<{ properties: readonly Property[] }>("/api/accounts/properties", {});
  return d.properties ?? [];
}

/** Accounts overview (portfolio or one property, optional inclusive period). */
export function apiAccountsOverview(
  propertyId?: string,
  period?: { from?: string; to?: string },
): Promise<AccountsOverviewData | ErrorEnvelope> {
  return acct("/api/accounts/overview", {
    ...acctProp(propertyId),
    ...(period?.from ? { from: period.from } : {}),
    ...(period?.to ? { to: period.to } : {}),
  });
}

/** Read one property's accounting settings (specified premises + mgmt fee). */
export function apiAccountsSettings(
  propertyId: string,
): Promise<AccountsPropertySettings | ErrorEnvelope> {
  return acct("/api/accounts/settings", { propertyId });
}

/** Update one property's accounting settings. */
export function apiAccountsUpdateSettings(input: {
  propertyId: string;
  gstSpecified?: boolean;
  managementFeePct?: number;
}): Promise<AccountsPropertySettings | ErrorEnvelope> {
  return acct("/api/accounts/settings/update", input);
}

/** Per-property cash-in-hand table. */
export async function apiAccountsCashInHand(
  propertyId?: string,
): Promise<readonly AccountsCashInHandRow[]> {
  const d = await acct<{ rows: readonly AccountsCashInHandRow[] }>(
    "/api/accounts/cash-in-hand",
    acctProp(propertyId),
  );
  return d.rows ?? [];
}

/** Sales-team performance. */
export async function apiAccountsSalesTeam(
  propertyId?: string,
): Promise<readonly AccountsSalesTeamRow[]> {
  const d = await acct<{ people: readonly AccountsSalesTeamRow[] }>(
    "/api/accounts/sales-team",
    acctProp(propertyId),
  );
  return d.people ?? [];
}

/** Book payroll (base + commission) as a Salaries & wages expense for a date. */
export function apiAccountsBookPayroll(input: {
  date: string;
  propertyId?: string;
}): Promise<{ booked: number; totalMinor: string } | ErrorEnvelope> {
  return acct("/api/accounts/payroll/book", input);
}

/** Chart-of-accounts grouped ledger. */
export async function apiAccountsChart(
  propertyId?: string,
): Promise<readonly AccountsChartGroup[]> {
  const d = await acct<{ groups: readonly AccountsChartGroup[] }>(
    "/api/accounts/chart",
    acctProp(propertyId),
  );
  return d.groups ?? [];
}

/** Stays & Invoices list. */
export async function apiAccountsStays(propertyId?: string): Promise<readonly AccountsStayRow[]> {
  const d = await acct<{ stays: readonly AccountsStayRow[] }>("/api/accounts/stays", acctProp(propertyId));
  return d.stays ?? [];
}

/** Upsert a stay invoice overlay. */
export function apiAccountsUpsertInvoice(input: {
  reservationId: string;
  propertyId: string;
  channel?: string;
  channelIsOta?: boolean;
  commissionPct?: number;
  collection?: "guest" | "ota";
  otaSettled?: boolean;
  discountMinor?: number;
  voucherCode?: string;
  currency?: string;
  salespersonId?: string | null;
  advanceMinor?: number;
  receivedOn?: string | null;
  lines: readonly {
    stream: AccountsStream;
    rateMinor: number;
    qty: number;
    description?: string;
    gstOverride?: number | "auto";
  }[];
}): Promise<CreateResult> {
  return acct("/api/accounts/invoice/upsert", input);
}

/** Record a payment against an invoice. */
export function apiAccountsRecordPayment(input: {
  invoiceId: string;
  amountMinor: number;
  method?: "cash" | "pos" | "gateway" | "bank";
  provider?: string;
  receivedOn?: string;
  reference?: string;
}): Promise<CreateResult> {
  return acct("/api/accounts/invoice/payment", input);
}

/** Set an invoice lifecycle (issued | archived | void). */
export function apiAccountsInvoiceLifecycle(input: {
  invoiceId: string;
  lifecycle: "issued" | "archived" | "void";
}): Promise<{ id: string; lifecycle: string } | ErrorEnvelope> {
  return acct("/api/accounts/invoice/lifecycle", input);
}

/** Delete an invoice (only when it has no recorded payments). */
export function apiAccountsInvoiceDelete(input: {
  invoiceId: string;
}): Promise<{ id: string } | ErrorEnvelope> {
  return acct("/api/accounts/invoice/delete", input);
}

/** Fetch full invoice detail (stay overlay + payment history). */
export function apiAccountsInvoiceDetail(
  reservationId: string,
): Promise<AccountsInvoiceDetail | ErrorEnvelope> {
  return acct("/api/accounts/invoice/detail", { reservationId });
}

/** Fetch the branded invoice HTML (for print / save as PDF). */
export function apiAccountsInvoicePdf(
  reservationId: string,
): Promise<{ html: string } | ErrorEnvelope> {
  return acct("/api/accounts/invoice/pdf", { reservationId });
}

/** Send the invoice by email (demo mailer; real provider pluggable). */
export function apiAccountsInvoiceEmail(input: {
  reservationId: string;
  to?: string;
  subject?: string;
  message?: string;
}): Promise<{ sent: boolean; to: string } | ErrorEnvelope> {
  return acct("/api/accounts/invoice/send-email", input);
}

/** Build a WhatsApp share deep-link for the invoice (client opens the URL). */
export function apiAccountsInvoiceWhatsApp(input: {
  reservationId: string;
  message?: string;
}): Promise<{ url: string; phone: string } | ErrorEnvelope> {
  return acct("/api/accounts/invoice/send-whatsapp", input);
}

/** Expenses list. */
export async function apiAccountsExpenses(propertyId?: string): Promise<readonly AccountsExpenseRow[]> {
  const d = await acct<{ expenses: readonly AccountsExpenseRow[] }>("/api/accounts/expenses", acctProp(propertyId));
  return d.expenses ?? [];
}

/** Create an expense. */
export function apiAccountsCreateExpense(input: {
  propertyId?: string;
  date: string;
  category: string;
  note?: string;
  amountMinor: number;
  gstRate?: number;
  method?: "cash" | "pos" | "gateway" | "bank";
  currency?: string;
  isDirect?: boolean;
  claimItc?: boolean;
  paidAccount?: string;
}): Promise<CreateResult> {
  return acct("/api/accounts/expenses/create", input);
}

/** Cash & Banks: movements + running balance. */
export function apiAccountsCash(
  propertyId?: string,
): Promise<{ rows: readonly AccountsCashRow[]; balanceMinor: string } | ErrorEnvelope> {
  return acct("/api/accounts/cash", acctProp(propertyId));
}

/** Record a cash movement. */
export function apiAccountsCreateCash(input: {
  propertyId?: string;
  date: string;
  kind: "opening" | "topup" | "deposit" | "count" | "adjustment";
  amountMinor: number;
  note?: string;
  currency?: string;
}): Promise<CreateResult> {
  return acct("/api/accounts/cash/create", input);
}

// ---- Cash & Banks workspace ----

/** Cash & Banks overview cards (cash-in-hand, bank, pending, variance). */
export function apiAccountsCashBanksOverview(
  propertyId?: string,
): Promise<AccountsCashBanksOverview | ErrorEnvelope> {
  return acct("/api/accounts/cash-banks/overview", acctProp(propertyId));
}

/** The physical-cash cashbook (running balance). */
export async function apiAccountsCashbook(
  propertyId?: string,
): Promise<readonly AccountsCashbookRow[]> {
  const d = await acct<{ rows: readonly AccountsCashbookRow[] }>("/api/accounts/cashbook", acctProp(propertyId));
  return d.rows ?? [];
}

/** Consolidated internal movements (cash + bank). */
export async function apiAccountsMovements(
  propertyId?: string,
): Promise<readonly AccountsMovementRow[]> {
  const d = await acct<{ rows: readonly AccountsMovementRow[] }>("/api/accounts/movements", acctProp(propertyId));
  return d.rows ?? [];
}

/** Bank accounts list (portfolio accounts always included). */
export async function apiAccountsBankAccounts(
  propertyId?: string,
): Promise<readonly AccountsBankAccountRow[]> {
  const d = await acct<{ accounts: readonly AccountsBankAccountRow[] }>("/api/accounts/bank-accounts", acctProp(propertyId));
  return d.accounts ?? [];
}

/** Create a bank account (only last 4 digits of the number are stored). */
export function apiAccountsCreateBankAccount(input: {
  propertyId?: string;
  name: string;
  bankName: string;
  accountType?: "current" | "savings" | "cash" | "other";
  accountNumber?: string;
  ifsc?: string;
  branch?: string;
  upiId?: string;
  purpose?: string;
  isDefault?: boolean;
  openingBalanceMinor?: number;
  openingAsOf?: string;
  note?: string;
}): Promise<CreateResult> {
  return acct("/api/accounts/bank-accounts/create", input);
}

/** Update a bank account's editable fields. */
export function apiAccountsUpdateBankAccount(input: {
  id: string;
  name?: string;
  bankName?: string;
  accountType?: "current" | "savings" | "cash" | "other";
  ifsc?: string | null;
  branch?: string | null;
  upiId?: string | null;
  purpose?: string | null;
  isDefault?: boolean;
  status?: "active" | "archived";
  note?: string | null;
}): Promise<CreateResult> {
  return acct("/api/accounts/bank-accounts/update", input);
}

/** Bank account detail (account + running-balance transactions). */
export function apiAccountsBankAccountDetail(
  bankAccountId: string,
): Promise<AccountsBankAccountDetail | ErrorEnvelope> {
  return acct("/api/accounts/bank-accounts/detail", { bankAccountId });
}

/** Record a cash deposit into a bank account (transfer). */
export function apiAccountsCashDeposit(input: {
  propertyId?: string;
  date: string;
  amountMinor: number;
  bankAccountId: string;
  reference?: string;
  note?: string;
}): Promise<{ transferGroupId: string } | ErrorEnvelope> {
  return acct("/api/accounts/cash-banks/deposit", input);
}

/** Record a bank withdrawal / cash top-up (transfer). */
export function apiAccountsBankWithdrawal(input: {
  propertyId?: string;
  date: string;
  amountMinor: number;
  bankAccountId: string;
  mode?: string;
  reference?: string;
  note?: string;
}): Promise<{ transferGroupId: string } | ErrorEnvelope> {
  return acct("/api/accounts/cash-banks/withdrawal", input);
}

/** Record a bank-to-bank transfer. */
export function apiAccountsBankTransfer(input: {
  fromBankAccountId: string;
  toBankAccountId: string;
  propertyId?: string;
  date: string;
  amountMinor: number;
  mode?: string;
  reference?: string;
  note?: string;
}): Promise<{ transferGroupId: string } | ErrorEnvelope> {
  return acct("/api/accounts/cash-banks/transfer", input);
}

/** Record an opening balance / float for cash or a bank account. */
export function apiAccountsOpeningBalance(input: {
  target: "cash" | "bank";
  bankAccountId?: string;
  propertyId?: string;
  date: string;
  amountMinor: number;
  note?: string;
}): Promise<CreateResult> {
  return acct("/api/accounts/cash-banks/opening", input);
}

/** Record an explicit balance adjustment (requires a reason). */
export function apiAccountsAdjustment(input: {
  target: "cash" | "bank";
  bankAccountId?: string;
  propertyId?: string;
  date: string;
  direction: "increase" | "decrease";
  amountMinor: number;
  reason: string;
  reference?: string;
}): Promise<CreateResult> {
  return acct("/api/accounts/cash-banks/adjustment", input);
}

/** Record a physical cash count (never mutates expected; variance stays visible). */
export function apiAccountsCashCount(input: {
  propertyId: string;
  date: string;
  countedMinor: number;
  denominations?: Record<string, number>;
  note?: string;
}): Promise<CreateResult> {
  return acct("/api/accounts/cash-banks/count", input);
}

/** Reverse a posted cash/bank movement (audit-retained; never a hard delete). */
export function apiAccountsReverseMovement(input: {
  source: "cash" | "bank";
  movementId: string;
}): Promise<{ reversed: number } | ErrorEnvelope> {
  return acct("/api/accounts/cash-banks/reverse", input);
}

/** Vouchers list. */
export async function apiAccountsVouchers(): Promise<readonly AccountsVoucherRow[]> {
  const d = await acct<{ vouchers: readonly AccountsVoucherRow[] }>("/api/accounts/vouchers", {});
  return d.vouchers ?? [];
}

/** Create/update a promo offer or gift voucher. */
export function apiAccountsUpsertVoucher(input: {
  code: string;
  active?: boolean;
  type: "pct" | "fixed";
  value: number;
  maxCapMinor?: number | null;
  minSpendMinor?: number | null;
  redemptionLimit?: number | null;
  directOnly?: boolean;
  validFrom?: string | null;
  validTo?: string | null;
  kind?: "promo" | "gift";
  name?: string | null;
  eligibility?: "all" | "new" | "returning";
  propertyScope?: "all" | "selected";
  propertyIds?: readonly string[];
  initialValueMinor?: number | null;
  notes?: string | null;
}): Promise<CreateResult> {
  return acct("/api/accounts/vouchers/upsert", input);
}

/** Activate / deactivate a voucher. */
export function apiAccountsSetVoucherActive(input: {
  id: string;
  active: boolean;
}): Promise<CreateResult> {
  return acct("/api/accounts/vouchers/set-active", input);
}

/** Archive a voucher (soft; preserves redemption history). */
export function apiAccountsArchiveVoucher(input: { id: string }): Promise<CreateResult> {
  return acct("/api/accounts/vouchers/archive", input);
}

/** A gift voucher's redemption history. */
export async function apiAccountsVoucherRedemptions(
  voucherId: string,
): Promise<readonly AccountsVoucherRedemptionRow[]> {
  const d = await acct<{ redemptions: readonly AccountsVoucherRedemptionRow[] }>(
    "/api/accounts/vouchers/redemptions",
    { voucherId },
  );
  return d.redemptions ?? [];
}

/** Redeem a gift voucher against a booking payable (partial redemption). */
export function apiAccountsRedeemGift(input: {
  code: string;
  date: string;
  payableMinor: number;
  invoiceId?: string | null;
  reservationId?: string | null;
  note?: string | null;
}): Promise<
  | {
      ok: boolean;
      appliedMinor?: number;
      remainingPayableMinor?: number;
      voucherRemainingMinor?: number;
      reason?: string;
    }
  | ErrorEnvelope
> {
  return acct("/api/accounts/vouchers/redeem-gift", input);
}

/** Validate + price a promo voucher against a booking context. */
export function apiAccountsCheckVoucher(input: {
  code: string;
  date: string;
  channelIsOta: boolean;
  amountMinor: number;
}): Promise<{ ok: boolean; discountMinor?: number; reason?: string } | ErrorEnvelope> {
  return acct("/api/accounts/vouchers/check", input);
}

/** Payroll people. */
export async function apiAccountsPayroll(propertyId?: string): Promise<readonly AccountsPayrollRow[]> {
  const d = await acct<{ people: readonly AccountsPayrollRow[] }>("/api/accounts/payroll", acctProp(propertyId));
  return d.people ?? [];
}

/** Add or update a staff / payroll person. */
export function apiAccountsAddPayroll(input: {
  id?: string;
  propertyId?: string;
  name: string;
  baseSalaryMinor: number;
  commissionPct?: number;
  currency?: string;
  active?: boolean;
  role?: string;
  employmentType?: AccountsEmploymentType;
  mobile?: string | null;
  email?: string | null;
  joiningDate?: string | null;
  commissionType?: AccountsCommissionType;
  commissionValueMinor?: number;
  paymentMethod?: AccountsStaffPaymentMethod;
  accountHolder?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  ifsc?: string | null;
  upiId?: string | null;
  pan?: string | null;
  notes?: string | null;
}): Promise<CreateResult> {
  return acct("/api/accounts/payroll/create", input);
}

/** Activate / deactivate a staff person (never deleted). */
export function apiAccountsSetPayrollActive(input: {
  id: string;
  active: boolean;
}): Promise<CreateResult> {
  return acct("/api/accounts/payroll/set-active", input);
}

/** Staff summary for the Team tab cards. */
export function apiAccountsStaffSummary(
  propertyId?: string,
): Promise<AccountsStaffSummary | ErrorEnvelope> {
  return acct("/api/accounts/staff/summary", acctProp(propertyId));
}

/** Preview the eligible payroll lines for a month + scope (persists nothing). */
export function apiAccountsPayrollPreview(input: {
  periodMonth: string;
  propertyId?: string;
}): Promise<{ lines: readonly AccountsPayrollLine[]; currency: string } | ErrorEnvelope> {
  return acct("/api/accounts/payroll/preview", input);
}

/** Confirm (generate) a payroll run — Draft/Ready; moves no money. */
export function apiAccountsRunPayroll(input: {
  periodMonth: string;
  propertyId?: string;
  lines?: readonly { personId: string; additionalMinor?: number; deductionsMinor?: number }[];
}): Promise<{ runId: string; lines: number } | ErrorEnvelope> {
  return acct("/api/accounts/payroll/run", input);
}

/** List payroll runs. */
export async function apiAccountsPayrollRuns(input?: {
  periodMonth?: string;
  propertyId?: string;
}): Promise<readonly AccountsPayrollRun[]> {
  const d = await acct<{ runs: readonly AccountsPayrollRun[] }>(
    "/api/accounts/payroll/runs",
    input ?? {},
  );
  return d.runs ?? [];
}

/** List the lines of one payroll run. */
export async function apiAccountsPayrollRunLines(
  runId: string,
): Promise<readonly AccountsPayrollLine[]> {
  const d = await acct<{ lines: readonly AccountsPayrollLine[] }>(
    "/api/accounts/payroll/run-lines",
    { runId },
  );
  return d.lines ?? [];
}

/** Record a salary payment against one payroll line (marks Paid; books once). */
export function apiAccountsPayPayroll(input: {
  lineId: string;
  paidOn: string;
  method: AccountsStaffPaymentMethod;
  bankAccountId?: string | null;
  paidAccount?: string | null;
  reference?: string | null;
  note?: string | null;
}): Promise<CreateResult> {
  return acct("/api/accounts/payroll/pay", input);
}

/** List payslips with optional filters. */
export async function apiAccountsPayslips(input?: {
  periodMonth?: string;
  propertyId?: string;
  personId?: string;
  paymentStatus?: "pending" | "paid";
}): Promise<readonly AccountsPayslipRow[]> {
  const d = await acct<{ payslips: readonly AccountsPayslipRow[] }>(
    "/api/accounts/payroll/payslips",
    input ?? {},
  );
  return d.payslips ?? [];
}

/** One payslip's full detail. */
export function apiAccountsPayslipDetail(
  lineId: string,
): Promise<AccountsPayslipDetail | ErrorEnvelope> {
  return acct("/api/accounts/payroll/payslip", { lineId });
}

/** Branded payslip HTML for print / save-as-PDF. */
export function apiAccountsPayslipPdf(
  lineId: string,
): Promise<{ html: string } | ErrorEnvelope> {
  return acct("/api/accounts/payroll/payslip-pdf", { lineId });
}

/** Send a payslip by email (demo mailer). */
export function apiAccountsPayslipEmail(input: {
  lineId: string;
  to?: string;
  subject?: string;
  message?: string;
}): Promise<{ sent: boolean; to: string } | ErrorEnvelope> {
  return acct("/api/accounts/payroll/payslip-email", input);
}

/** Get a wa.me link to share a payslip by WhatsApp. */
export function apiAccountsPayslipWhatsApp(input: {
  lineId: string;
}): Promise<{ url: string; phone: string } | ErrorEnvelope> {
  return acct("/api/accounts/payroll/payslip-whatsapp", input);
}

/** Owner statements (optionally scoped to a period; values respect it). */
export async function apiAccountsOwnerStatements(
  propertyId?: string,
  period?: { from?: string; to?: string },
): Promise<readonly AccountsOwnerStatementRow[]> {
  const d = await acct<{ statements: readonly AccountsOwnerStatementRow[] }>(
    "/api/accounts/owner-statements",
    {
      ...acctProp(propertyId),
      ...(period?.from ? { from: period.from } : {}),
      ...(period?.to ? { to: period.to } : {}),
    },
  );
  return d.statements ?? [];
}

/** Ledger lines. */
export async function apiAccountsLedger(propertyId?: string): Promise<readonly AccountsLedgerLine[]> {
  const d = await acct<{ lines: readonly AccountsLedgerLine[] }>("/api/accounts/ledger", acctProp(propertyId));
  return d.lines ?? [];
}
